import React, {useEffect, useState, useCallback, useRef, useMemo, Profiler} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {App as AntdApp, Button, Card, Checkbox, Collapse, Dropdown, Empty, Input, Popover, Segmented, Space, Spin, Typography, Tooltip, Divider, InputNumber, Slider} from "antd";
import {ArrowLeftOutlined, ZoomInOutlined, ZoomOutOutlined, LeftOutlined, RightOutlined, DeleteOutlined, CloudUploadOutlined, DisconnectOutlined, RedoOutlined, AppstoreOutlined, BgColorsOutlined, DownOutlined, NumberOutlined, SearchOutlined, PlusOutlined} from "@ant-design/icons";
import {Stage, Layer, Image as KonvaImage, Rect, Group} from "react-konva";
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	DragOverlay,
	defaultDropAnimationSideEffects
} from "@dnd-kit/core";
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
	useSortable
} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import {restrictToVerticalAxis} from "@dnd-kit/modifiers";
import {MenuOutlined} from "@ant-design/icons";
import api from "@/api/axios.js";
import {SortablePageThumb} from "../components/PageThumb.jsx";
import usePageManagement from "../hooks/usePageManagement.jsx";
import UploadPdfModal from "../components/UploadPdfModal.jsx";
import AddPagesModal from "../components/AddPagesModal.jsx";
import ManualFieldModal from "../components/ManualFieldModal.jsx";
import ManualZoneBadge from "../components/ManualZoneBadge.jsx";
import {useFieldColorMap, getFieldColor} from "@modules/fields/utils/fieldColors.js";
import {fetchBannerFields} from "@modules/settings/api/settingsApi.js";
import {
	applyManualFieldsToZone,
	collectSharedInitialValues,
	fetchBannerValuesBySerial,
	readBannerZoneValue
} from "@modules/flyers/utils/zoneManualFields.js";
import {resolvePreviewScale} from "@modules/flyers/utils/previewScale.js";
import {formatPdfPageSize} from "@modules/flyers/utils/pdfPageSize.js";
import ZoneContextMenu from "../components/ZoneContextMenu.jsx";
import EditSerialModal from "../components/EditSerialModal.jsx";
import {getZoneSearchState, zoneSearchHighlightProps} from "../utils/zoneSearchHighlight.js";
import PageJumper from "../components/PageJumper.jsx";
import ZoneSerialBadge from "../components/ZoneSerialBadge.jsx";
import ZoneSelectionTransformer from "../components/ZoneSelectionTransformer.jsx";
import ShortcutsLegend from "../components/ShortcutsLegend.jsx";
import {applyZoneGeometryUpdates, clampZoneToPage, getZoneScaleFactor, scaleZonesFromCenter} from "../utils/zoneGroupGeometry.js";
import {useSSE} from "@modules/realtime/hooks/useSSE.js";
import "./FlyerPreprocessPage.css";

const {Title, Text} = Typography;

// Colori base per i rettangoli
const BASE_RECT_COLORS = {
	banner: {
		stroke: "#22c55e",
		fill: "rgba(34, 197, 94, 1)", // L'opacità sarà applicata dinamicamente
	},
	gabbia: {
		stroke: "#3b82f6",
		fill: "rgba(59, 130, 246, 1)",
	},
	// Gabbia la cui estrazione AI è fallita: finora sul canvas restava identica alle altre e
	// l'unico segnale era nel menu "Sblocca errore", quindi individuarla richiedeva di aprire
	// l'editor una per una. Stesso rosso usato in VariantPreprocessPage.
	error: {
		stroke: "#ff4d4f",
		fill: "rgba(255, 77, 79, 1)",
	},
	// Marcata per la rielaborazione (qui ci finisce riordinando le gabbie di una pagina, che
	// sposta gli slot e obbliga a rigenerare): era indistinguibile da una gabbia qualsiasi,
	// quindi non c'era modo di sapere cosa il prossimo passaggio AI avrebbe rifatto. Ambra,
	// come in VariantPreprocessPage; l'arancio tratteggiato resta l'elaborazione IN CORSO.
	reprocess: {
		stroke: "#f59e0b",
		fill: "rgba(245, 158, 11, 1)",
	}
};

function getZoneColor(zone)
{
	if (zone?.status === "error") return BASE_RECT_COLORS.error;
	if (zone?.status === "reprocess") return BASE_RECT_COLORS.reprocess;
	return BASE_RECT_COLORS[zone?.type] || BASE_RECT_COLORS.gabbia;
}

const PAGE_SPACING = 40; // Spazio tra le pagine
const DEFAULT_SCALE = 1;
// Finestra entro cui pressioni di freccia consecutive vengono coalizzate in un unico passo di undo.
const ARROW_COALESCE_MS = 500;

// Margine (px schermo) attorno alla pagina nel canvas, uguale su ogni lato: la
// pagina resta centrata con lo stesso "respiro" a sinistra/destra e sopra/sotto.
const STAGE_MARGIN = 50;

function clamp(value, min, max)
{
	return Math.min(Math.max(value, min), max);
}

// Firma strutturale di un set di zone (id + pagina + tipo + geometria arrotondata): serve allo
// storico "Annulla" per registrare un passo solo quando cambia la struttura/geometria, ignorando
// le variazioni dei soli campi manuali (manualOverrides) o dello stato editoriale.
function structuralZonesSig(zones)
{
	return JSON.stringify((zones || []).map((z) => [
		z.id, z.pageIndex, z.type, Math.round(z.x), Math.round(z.y), Math.round(z.w), Math.round(z.h)
	]));
}

function reorderZonesWithinPage(items, pageIndex, activeId, overId)
{
	const pageZoneIds = items.filter((item) => item.pageIndex === pageIndex).map((item) => item.id);
	const oldIndex = pageZoneIds.findIndex((id) => id === activeId);
	const newIndex = pageZoneIds.findIndex((id) => id === overId);

	if (oldIndex === -1 || newIndex === -1) return items;

	const reorderedPageIds = arrayMove(pageZoneIds, oldIndex, newIndex);
	const reorderedPageZones = reorderedPageIds
		.map((id) => items.find((item) => item.id === id))
		.filter(Boolean);

	let pageCursor = 0;
	return items.map((item) =>
	{
		if (item.pageIndex !== pageIndex) return item;
		const nextZone = reorderedPageZones[pageCursor];
		pageCursor += 1;
		return nextZone || item;
	});
}

// Tipi di <input> che sono widget (checkbox, radio, bottoni...) e non campi di testo: il focus
// su questi elementi non deve bloccare le scorciatoie da tastiera della pagina (Canc, frecce...).
const NON_TEXT_INPUT_TYPES = new Set(["checkbox", "radio", "button", "submit", "reset", "range", "color", "file", "image"]);

function isEditableTarget(target)
{
	if (!(target instanceof HTMLElement)) return false;
	if (target.isContentEditable) return true;
	if (target.closest?.("[contenteditable='true']")) return true;

	const tagName = target.tagName;
	if (tagName === "INPUT")
		return !NON_TEXT_INPUT_TYPES.has((target.type || "text").toLowerCase());
	return tagName === "TEXTAREA" || tagName === "SELECT";
}

function hasSelectionModifier(event)
{
	const nativeEvent = event?.evt || event;
	return Boolean(nativeEvent?.ctrlKey || nativeEvent?.metaKey || nativeEvent?.shiftKey);
}

/**
 * Componente per un singolo rettangolo tracciabile. Le maniglie di ridimensionamento non sono
 * qui: le disegna ZoneSelectionTransformer, unico per tutta la selezione. Con più gabbie
 * selezionate Konva notifica fine trascinamento/ridimensionamento a ciascuna: il genitore le
 * raccoglie e le applica in un colpo solo.
 */
function TraceableRect({shapeProps, isSelected, onSelect, onChange, onContextMenu, style, isProcessing, searchState = null, zoom = 1})
{
	const shapeRef = useRef();

	// const style = RECT_STYLES[shapeProps.type] || RECT_STYLES.gabbia; (Ora passato da parent)

	return (
		<>
			<Rect
				onClick={onSelect}
				onTap={onSelect}
				onContextMenu={onContextMenu}
				ref={shapeRef}
				{...shapeProps}
				{...style}
				{...(isProcessing ? {stroke: "#fa8c16", strokeWidth: Math.max(style?.strokeWidth || 3, 3), fill: "rgba(250, 140, 22, 0.28)", dash: [10, 6]} : {})}
				shadowColor={isSelected ? (isProcessing ? "#fa8c16" : style.stroke) : undefined}
				shadowBlur={isSelected ? 8 : 0}
				shadowOpacity={isSelected ? 0.6 : 0}
				{...zoneSearchHighlightProps(searchState, zoom)}
				draggable={!isProcessing}
				onDragEnd={(e) =>
				{
					// Konva ha già spostato il nodo dove è stato lasciato il mouse. Il genitore
					// vincola la gabbia dentro i bordi pagina: se il risultato coincide con la
					// posizione che la gabbia aveva GIÀ (trascinamento tutto fuori pagina, che il
					// clamp riporta esattamente dov'era), nessuna prop cambia, react-konva non
					// riapplica nulla e il nodo resta disegnato fuori pagina pur essendo salvato
					// dentro. Si riallinea sempre il nodo alle prop correnti: se lo stato cambia
					// davvero, è il ri-render subito dopo a portarlo nella posizione nuova.
					const node = e.target;
					const dropped = {x: node.x(), y: node.y()};
					node.position({x: shapeProps.x, y: shapeProps.y});

					onChange({
						...shapeProps,
						x: dropped.x,
						y: dropped.y
					});
				}}
				onTransformEnd={() =>
				{
					const node = shapeRef.current;
					const scaleX = node.scaleX();
					const scaleY = node.scaleY();
					const resized = {
						x: node.x(),
						y: node.y(),
						width: Math.max(10, node.width() * scaleX),
						height: Math.max(10, node.height() * scaleY)
					};
					// Stesso riallineamento del trascinamento: un ridimensionamento interamente
					// fuori pagina viene riportato dal clamp sulla geometria precedente, quindi
					// senza questo ripristino il rettangolo resterebbe disegnato deformato.
					node.scaleX(1);
					node.scaleY(1);
					node.position({x: shapeProps.x, y: shapeProps.y});
					node.width(shapeProps.width);
					node.height(shapeProps.height);

					onChange({...shapeProps, ...resized});
				}}
			/>
		</>
	);
}

/**
 * Contenuto della card gabbia, condiviso fra la voce dell'accordion e il risultato di
 * ricerca: stesso aspetto e stesse azioni (pallino di stato, seriale, pagina/tipo,
 * eliminazione da selezionata), così una gabbia trovata cercando si maneggia esattamente
 * come una raggiunta sfogliando le pagine. `description` compare solo nei risultati.
 */
function ZoneItemContent({zone, isSelected, isProcessing, onDelete, description})
{
	return (
		<>
			{isProcessing
				? <Spin size="small" className="zone-spinner"/>
				: <div className="zone-indicator" style={{backgroundColor: getZoneColor(zone).stroke}}/>}
			<div className="zone-info">
				<div className="zone-info-head">
					<Text strong style={{fontSize: 12}}>#{zone.id}</Text>
					<Text type="secondary" style={{fontSize: 10}}>P{zone.pageIndex + 1} ({zone.type})</Text>
				</div>
				{description && (
					<Text type="secondary" className="zone-desc" title={description}>{description}</Text>
				)}
			</div>
			{isSelected && (
				<DeleteOutlined
					className="zone-delete-icon"
					onClick={(e) =>
					{
						e.stopPropagation();
						onDelete(zone.id);
					}}
				/>
			)}
		</>
	);
}

/**
 * Risultato di ricerca: stessa card della lista, senza maniglia di trascinamento
 * (il riordino vale dentro una pagina, i risultati attraversano tutto il volantino).
 */
function ZoneResultItem({zone, isSelected, isProcessing, onSelect, onDelete, description})
{
	return (
		<div
			className={`zone-item zone-result-item ${isSelected ? "selected" : ""} ${isProcessing ? "processing" : ""}`}
			onClick={onSelect}
		>
			<ZoneItemContent
				zone={zone}
				isSelected={isSelected}
				isProcessing={isProcessing}
				onDelete={onDelete}
				description={description}
			/>
		</div>
	);
}

/**
 * Singolo elemento della lista zone (Sortable)
 */
function SortableZoneItem({zone, isSelected, isProcessing, onSelect, onDelete})
{
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging
	} = useSortable({id: zone.id});

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
		zIndex: isDragging ? 100 : 1,
	};

	return (
		<div
			ref={setNodeRef}
			style={style}
			className={`zone-item ${isSelected ? "selected" : ""} ${isProcessing ? "processing" : ""}`}
			onClick={onSelect}
		>
			<div className="zone-drag-handle" {...attributes} {...listeners}>
				<MenuOutlined style={{color: '#bfbfbf', fontSize: 12}}/>
			</div>
			<ZoneItemContent
				zone={zone}
				isSelected={isSelected}
				isProcessing={isProcessing}
				onDelete={onDelete}
			/>
		</div>
	);
}

/**
 * Pagina principale di pre-elaborazione (Refactored: Single Canvas)
 */
export default function FlyerPreprocessPage()
{
	const {flyerId} = useParams();
	const navigate = useNavigate();
	const {message, modal} = AntdApp.useApp();

	const [loading, setLoading] = useState(true);
	const [pages, setPages] = useState([]);
	const [zones, setZones] = useState([]);
	// promoId vivo per le prenotazioni seriali atomiche dentro handler async
	// (evita closure stantie). I seriali NON sono più stimati lato client.
	const promoIdRef = useRef(null);
	// Volantino da cui le `zones` in stato sono state effettivamente caricate: l'autosave scrive solo
	// se coincide con :flyerId corrente. La pagina non fa remount al cambio di volantino, quindi senza
	// questo controllo una fetch fallita lascerebbe in stato le gabbie del volantino precedente e le
	// salverebbe su quello nuovo.
	const loadedFlyerIdRef = useRef(null);
	const [selectedIds, setSelectedIds] = useState([]);
	const [activeType, setActiveType] = useState("gabbia");
	const [scale, setScale] = useState(() =>
	{
		try
		{
			const stored = parseFloat(localStorage.getItem("preprocess.zoom"));
			if (Number.isFinite(stored)) return stored;
		}
		catch
		{
		}
		return DEFAULT_SCALE;
	});
	const [saving, setSaving] = useState(false);
	const [pdfModalOpen, setPdfModalOpen] = useState(false);
	const [aiProcessing, setAiProcessing] = useState(false);
	const [flyerState, setFlyerState] = useState(null);
	const [unlockingProcessing, setUnlockingProcessing] = useState(false);
	const [ocrProgress, setOcrProgress] = useState(0);
	// Seriali (= zone.id) attualmente in elaborazione AI: usato per evidenziare le card sul canvas/sidebar.
	const [processingSerials, setProcessingSerials] = useState(() => new Set());
	// Context menu tasto destro su una zona: {x, y, targetIds:number[]} | null
	const [zoneMenu, setZoneMenu] = useState(null);
	// Gabbia in rinumerazione manuale (modale "Modifica seriale"), null = chiusa.
	const [serialEditZone, setSerialEditZone] = useState(null);
	// Campi "manuali" della cooperativa (allowed in /org con AI effettiva = false) mostrati nel menu contestuale.
	const [manualFields, setManualFields] = useState([]);
	// Campi delle gabbie banner (tutti, per le etichette dei tag sulle gabbie); nel menu
	// contestuale e nella modale compaiono solo quelli visibili al tasto destro.
	const [bannerFields, setBannerFields] = useState([]);
	const bannerMenuFields = useMemo(() => bannerFields.filter((f) => f.contextMenu), [bannerFields]);
	// Mappa chiave→nome campo, per etichettare i tag sulle gabbie ("Nome campo: valore").
	// Le stesse chiavi possono avere nomi diversi sulle due superfici (es. banner_video è un
	// campo prodotto per alcuni clienti): ogni gabbia usa prima i nomi del proprio tipo.
	const fieldNamesByZoneType = useMemo(() =>
	{
		const toMap = (list) => Object.fromEntries(list.filter((f) => f?.key).map((f) => [f.key, f.name || f.key]));
		const productNames = toMap(manualFields);
		const bannerNames = toMap(bannerFields);
		return {
			gabbia: {...bannerNames, ...productNames},
			banner: {...productNames, ...bannerNames}
		};
	}, [manualFields, bannerFields]);
	// Colore configurato in Impostazioni > Descrizioni AI per ciascun campo, per i tag
	// sulle gabbie (stessa fonte usata da editor card/gestione volantini/selezione massiva).
	const fieldColorMap = useFieldColorMap();
	const fieldColorByKey = useMemo(() =>
	{
		const map = {};
		for (const f of [...manualFields, ...bannerFields])
			if (f?.key)
				map[f.key] = getFieldColor(fieldColorMap, f.key);
		return map;
	}, [manualFields, bannerFields, fieldColorMap]);
	// Modale di inserimento valore manuale: {field, targetIds:number[], initialValue} | null
	const [manualModal, setManualModal] = useState(null);
	const [previewScale, setPreviewScale] = useState(1);
	// Larghezza raster vera della pagina (0 se il server non la manda): unita' di misura per
	// ricavare la scala effettiva dal rapporto con l'anteprima caricata, invece di fidarsi
	// del previewScale salvato che puo' non corrispondere piu' ai file di anteprima su disco.
	const [rasterPageWidth, setRasterPageWidth] = useState(0);
	const [pdfPageSize, setPdfPageSize] = useState(null);
	const [currentPageIndex, setCurrentPageIndex] = useState(0);
	const [isSidebarOpen, setIsSidebarOpen] = useState(() =>
	{
		try
		{
			const stored = localStorage.getItem("preprocess.pages.sidebar.open");
			if (stored === "false") return false;
		}
		catch
		{
		}
		return true;
	});

	// Configurazione rettangoli
	const [rectStrokeWidth, setRectStrokeWidth] = useState(3);
	const [rectOpacity, setRectOpacity] = useState(0.3);

	// ─── Ricerca gabbie su TUTTO il volantino ───
	// La sidebar è un accordion per pagina: senza ricerca, trovare un prodotto significa
	// aprire una pagina per volta. La query cerca invece su tutte le pagine insieme e ogni
	// risultato dichiara la propria. I testi prodotto NON stanno nelle zone (che portano solo
	// geometria e seriale) ma nelle FlyerCard, quindi vanno caricati a parte: si fa alla prima
	// interazione con il campo, per non appesantire l'apertura della pagina a chi non cerca.
	const [zoneQuery, setZoneQuery] = useState("");
	const [cardTextBySerial, setCardTextBySerial] = useState(null); // null = mai caricato
	const [cardTextLoading, setCardTextLoading] = useState(false);
	// Guardia SINCRONA contro le richieste doppie: `cardTextLoading` è stato, quindi si aggiorna
	// dopo il render — due esecuzioni ravvicinate dell'effetto (digitazione veloce, o il doppio
	// invoke di StrictMode in sviluppo) passerebbero entrambe. Il ref si aggiorna subito.
	const cardTextFetchRef = useRef(false);

	// ─── Seriali sul canvas, preferenza persistente (stessa chiave-stile della variante) ───
	const [showSerials, setShowSerials] = useState(() =>
	{
		try
		{
			return localStorage.getItem("fpp.showSerials") !== "0";
		}
		catch
		{
			return true;
		}
	});

	useEffect(() =>
	{
		try
		{
			localStorage.setItem("fpp.showSerials", showSerials ? "1" : "0");
		}
		catch
		{
		}
	}, [showSerials]);

	// Riferimenti per anteprima rettangolo (evita re-render ad ogni mousemove)
	const previewRectRef = useRef(null);
	const previewRectAttrsRef = useRef(null);
	const previewRafRef = useRef(0);
	const clipboardZoneRef = useRef(null);
	const lastPointerPosRef = useRef(null);

	// Storico per "Annulla"/"Rifai" (Cmd/Ctrl+Z e Cmd/Ctrl+Shift+Z): due pile di snapshot delle
	// zone. Registriamo solo i cambi STRUTTURALI (creazione/spostamento/ridimensionamento/
	// eliminazione/incolla/riordino), non le sole modifiche ai campi manuali. restoringRef evita
	// che il ripristino (undo o redo) venga registrato come nuova azione. prevZonesRef tiene lo
	// stato corrente/precedente, usato come sorgente affidabile (mai closure stantie) per impilare.
	// Ogni NUOVA azione dell'utente svuota redoStackRef (i "futuri" annullati non sono più validi).
	const undoStackRef = useRef([]);
	const redoStackRef = useRef([]);
	const prevZonesRef = useRef([]);
	const restoringRef = useRef(false);
	const zonesRef = useRef([]);
	// Coalescing delle frecce: suppressHistoryRef fa saltare all'effetto la registrazione dei
	// movimenti intermedi; arrowBurstRef indica una sequenza di frecce in corso; arrowTimerRef la
	// chiude dopo una breve pausa. Così un'intera "nudge session" con le frecce = un solo undo.
	// Vale anche per la scala da tastiera (Cmd+. / Cmd+,): burstKindRef distingue le due
	// sequenze, così frecce e scala ravvicinate restano due passi di undo separati.
	const suppressHistoryRef = useRef(false);
	const arrowBurstRef = useRef(false);
	const arrowTimerRef = useRef(null);
	const burstKindRef = useRef(null);
	// Geometrie in arrivo dalla fine di un trascinamento/ridimensionamento sul canvas, raccolte
	// per gabbia e applicate insieme (vedi queueZoneGeometry).
	const pendingGeometryRef = useRef(new Map());

	// Caricamento immagini
	const [loadedImages, setLoadedImages] = useState({});
	const [pageLayout, setPageLayout] = useState({offsets: [], totalHeight: 0, maxWidth: 0});

	// Riferimenti per disegno
	const stageRef = useRef();
	const scrollContainerRef = useRef();
	const isDrawing = useRef(false);
	// Cmd/Ctrl/Shift + trascina su area vuota: selezione multipla a rettangolo (marquee).
	// Stato separato da isDrawing perché è un gesto diverso (seleziona, non traccia).
	const isMarqueeSelecting = useRef(false);
	const startPos = useRef(null);
	const perfEnabledRef = useRef(false);
	const perfLastLogRef = useRef(0);
	const perfStatsRef = useRef({pages: 0, zones: 0, scale: 1});
	const drawStartRef = useRef(0);
	const suppressAutoPageRef = useRef(false);
	const pendingFocusZoneRef = useRef(null);
	const pendingAiStartRef = useRef(false);

	// Sensori per D&D
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 5, // Evita drag accidentali al click
			}
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	useEffect(() =>
	{
		if (typeof window === "undefined") return;
		perfEnabledRef.current = import.meta.env.DEV && window.localStorage?.getItem("preprocessPerf") === "1";
	}, []);

	useEffect(() =>
	{
		perfStatsRef.current = {pages: pages.length, zones: zones.length, scale};
	}, [pages.length, zones.length, scale]);

	const logPerf = useCallback((label, payload) =>
	{
		if (!perfEnabledRef.current) return;
		const now = performance.now();
		if (now - perfLastLogRef.current < 1500) return;
		perfLastLogRef.current = now;
		console.log(`[preprocess-perf] ${label}`, payload);
	}, []);

	const handleProfilerRender = useCallback((id, phase, actualDuration, baseDuration) =>
	{
		if (!perfEnabledRef.current) return;
		const stats = perfStatsRef.current;
		logPerf("render", {
			id,
			phase,
			actualDurationMs: Math.round(actualDuration),
			baseDurationMs: Math.round(baseDuration),
			pages: stats.pages,
			zones: stats.zones,
			scale: stats.scale
		});
	}, [logPerf]);

	const previewStyle = useMemo(() =>
	{
		const baseStyle = BASE_RECT_COLORS[activeType] || BASE_RECT_COLORS.gabbia;
		return {
			stroke: baseStyle.stroke,
			strokeWidth: rectStrokeWidth,
			fill: baseStyle.fill.replace("1)", `${rectOpacity})`),
			dash: [5, 5]
		};
	}, [activeType, rectStrokeWidth, rectOpacity]);

	// Stile del rettangolo di selezione (marquee): blu neutro, non il colore di banner/gabbia —
	// deve leggersi come "sto selezionando", non come un'anteprima di nuova gabbia/banner.
	const marqueeStyle = {stroke: "#1677ff", strokeWidth: 1, fill: "rgba(22, 119, 255, 0.08)", dash: [4, 4]};

	const hidePreviewRect = useCallback(() =>
	{
		const rect = previewRectRef.current;
		if (!rect) return;
		rect.visible(false);
		rect.getLayer()?.batchDraw();
	}, []);

	const previewRectStyleRef = useRef(previewStyle);

	const updatePreviewRect = useCallback((attrs, styleOverride) =>
	{
		previewRectAttrsRef.current = attrs;
		previewRectStyleRef.current = styleOverride || previewStyle;
		if (previewRafRef.current) return;
		previewRafRef.current = requestAnimationFrame(() =>
		{
			previewRafRef.current = 0;
			const rect = previewRectRef.current;
			const next = previewRectAttrsRef.current;
			if (!rect || !next) return;
			rect.setAttrs({...next, ...previewRectStyleRef.current, visible: true});
			rect.getLayer()?.batchDraw();

			if (perfEnabledRef.current && drawStartRef.current)
			{
				const stats = perfStatsRef.current;
				const durationMs = performance.now() - drawStartRef.current;
				logPerf("draw-commit", {
					durationMs: Math.round(durationMs),
					pages: stats.pages,
					zones: stats.zones,
					scale: stats.scale
				});
				drawStartRef.current = 0;
			}
		});
	}, [logPerf, previewStyle]);

	useEffect(() =>
	{
		return () =>
		{
			if (previewRafRef.current)
			{
				cancelAnimationFrame(previewRafRef.current);
				previewRafRef.current = 0;
			}
		};
	}, []);

	const syncFlyerProcessingUi = useCallback((flyer, {preservePendingStart = false} = {}) =>
	{
		const isActive = flyer?.status === "PROCESSING" && !flyer?.isProcessingStale;
		if (!isActive && preservePendingStart && pendingAiStartRef.current)
			return;

		if (isActive || flyer?.status === "ERROR" || flyer?.status === "READY" || flyer?.status === "NEW" || flyer?.status === "DONE")
			pendingAiStartRef.current = false;

		setAiProcessing(isActive);
		if (isActive)
		{
			const progress = Number(flyer?.processingProgress) || 0;
			setOcrProgress(progress > 0 ? progress : 5);
			return;
		}

		if (flyer?.status === "ERROR" || flyer?.status === "READY" || flyer?.status === "NEW" || flyer?.status === "DONE")
			setOcrProgress(0);
	}, []);

	const loadFlyerState = useCallback(async ({preservePendingStart = false} = {}) =>
	{
		const {data: flyerData} = await api.get(`/api/flyers/${flyerId}`);
		const flyer = flyerData?.flyer || flyerData;
		setFlyerState(flyer);
		syncFlyerProcessingUi(flyer, {preservePendingStart});

		// Seriale promo-unico: la numerazione è assegnata atomicamente dal server
		// alla creazione di ogni gabbia (prenotazione), non più stimata qui.
		promoIdRef.current = flyer?.promoId ? String(flyer.promoId) : null;

		return flyer;
	}, [flyerId, syncFlyerProcessingUi]);

	// 1. Carica dati iniziali dal server
	const fetchData = useCallback(async () =>
	{
		setLoading(true);
		setLoadedImages({});
		// Nuovo volantino: azzera lo storico Annulla/Rifai (la pagina non fa remount al cambio di
		// :flyerId, quindi i ref sopravviverebbero e si "annullerebbe" verso le zone del precedente).
		undoStackRef.current = [];
		redoStackRef.current = [];
		prevZonesRef.current = [];
		suppressHistoryRef.current = false;
		arrowBurstRef.current = false;
		if (arrowTimerRef.current) clearTimeout(arrowTimerRef.current);
		try
		{
			const res = await api.get(`/api/flyers/${flyerId}/preprocess`);
			if (res.data?.ok)
			{
				const nextPages = res.data.pages || [];
				setPages(nextPages);
				setZones(res.data.preprocessZones || []);
				setPreviewScale(res.data.previewScale || 1);
				setRasterPageWidth(res.data.rasterPageWidth || 0);
				setPdfPageSize(formatPdfPageSize(res.data.preprocessConfig));
				if (res.data.preprocessConfig)
				{
					setRectStrokeWidth(res.data.preprocessConfig.rectStrokeWidth || 3);
					setRectOpacity(res.data.preprocessConfig.rectOpacity || 0.3);
				}
				loadedFlyerIdRef.current = flyerId;
				// Volantino senza PDF caricato: nessuna immagine da attendere, chiudi il loader qui.
				// La decisione va presa SOLO a risposta ricevuta: `pages` vuoto prima di questo punto
				// significa "fetch ancora in volo", e spegnere `loading` sbloccherebbe l'autosave
				// mentre `zones` è ancora l'array vuoto iniziale (salvataggio a vuoto sulle gabbie).
				if (!nextPages.length) setLoading(false);
			}

			await loadFlyerState({preservePendingStart: true});
		}
		catch (err)
		{
			setLoading(false);
			message.error("Errore caricamento: " + (err.message || "Sconosciuto"));
		}
		}, [flyerId, loadFlyerState, message]);

	// Ref sempre allineata all'ultima fetchData: gli handler SSE sono catturati al PRIMO
	// montaggio (useSSE si risottoscrive solo al cambio di topic o di chiavi degli handler),
	// quindi chiamarla direttamente dall'handler significherebbe invocare per sempre la
	// closure iniziale — con il flyerId e le dipendenze di allora.
	const fetchDataRef = useRef(fetchData);
	fetchDataRef.current = fetchData;

	useEffect(() =>
	{
		fetchData();
	}, [fetchData]);

	useEffect(() =>
	{
		try
		{
			localStorage.setItem("preprocess.zoom", String(scale));
		}
		catch
		{
		}
	}, [scale]);

	useEffect(() =>
	{
		try
		{
			localStorage.setItem("preprocess.pages.sidebar.open", String(isSidebarOpen));
		}
		catch
		{
		}
	}, [isSidebarOpen]);

	useEffect(() =>
	{
		if (pages.length > 0)
		{
			setCurrentPageIndex(0);
		}
	}, [pages]);

	// Chiude il context menu al cambio pagina (le coordinate viewport diventano stale).
	useEffect(() => { setZoneMenu(null); }, [currentPageIndex]);

	// Chiusura del context menu su click/tasto destro esterni, scroll o Escape.
	// Il Dropdown con trigger=[] non gestisce da sé il click-outside, quindi va fatto a mano.
	// I listener si registrano in questo effect, che scatta DOPO il commit del render di apertura
	// (quindi dopo che l'evento contextmenu che ha aperto il menu è già stato processato): non
	// serve ritardarli. Un tasto destro su un'altra gabbia lo riposiziona (handleZoneContextMenu
	// rilancia setZoneMenu nello stesso ciclo, quindi prevale la nuova posizione).
	useEffect(() =>
	{
		if (!zoneMenu) return undefined;

		const closeIfOutside = (e) =>
		{
			if (e.target?.closest?.(".zone-ctx-menu")) return; // dentro al menu: gestisce l'onClick della voce
			setZoneMenu(null);
		};
		const closeNow = () => setZoneMenu(null);
		const onKey = (e) => { if (e.key === "Escape") setZoneMenu(null); };

		window.addEventListener("mousedown", closeIfOutside, true);
		window.addEventListener("contextmenu", closeIfOutside, true);
		window.addEventListener("scroll", closeNow, true);
		window.addEventListener("keydown", onKey);

		return () =>
		{
			window.removeEventListener("mousedown", closeIfOutside, true);
			window.removeEventListener("contextmenu", closeIfOutside, true);
			window.removeEventListener("scroll", closeNow, true);
			window.removeEventListener("keydown", onKey);
		};
	}, [zoneMenu]);

	// Carica i campi "manuali" (allowed con AI effettiva = false) della cooperativa del flyer.
	useEffect(() =>
	{
		const cooperativaId = flyerState?.cooperativaId;
		if (!cooperativaId)
		{
			setManualFields([]);
			return undefined;
		}

		let active = true;
		(async () =>
		{
			try
			{
				const {data} = await api.get(`/api/orgnodes/${cooperativaId}/manual-fields`);
				if (active)
					setManualFields(Array.isArray(data?.fields) ? data.fields : []);
			}
			catch
			{
				if (active)
					setManualFields([]);
			}
		})();

		return () => { active = false; };
	}, [flyerState?.cooperativaId]);

	// Campi delle gabbie banner: uguali per tutti i clienti, con la scelta di quali compaiono al
	// tasto destro (Impostazioni → Descrizione banner).
	useEffect(() =>
	{
		let active = true;
		fetchBannerFields()
			.then((fields) => { if (active) setBannerFields(fields); })
			.catch(() => { if (active) setBannerFields([]); });

		return () => { active = false; };
	}, []);

	// Safety-net: a elaborazione conclusa azzera lo stato visivo per-zona (nessuna card "appesa").
	useEffect(() => { if (!aiProcessing) setProcessingSerials(new Set()); }, [aiProcessing]);

	useSSE(flyerId ? `flyer:${flyerId}` : null, {
		"flyer.card.processing": (p) =>
		{
			if (p?.seriale != null)
				setProcessingSerials(prev => { const next = new Set(prev); next.add(p.seriale); return next; });
		},
		"flyer.card.created": (p) =>
		{
			const ser = p?.card?.seriale;
			if (ser != null)
				setProcessingSerials(prev => { const next = new Set(prev); next.delete(ser); return next; });
		},
		"flyer.card.error": (p) =>
		{
			if (p?.seriale != null)
				setProcessingSerials(prev => { const next = new Set(prev); next.delete(p.seriale); return next; });
		},
		// Le pagine sono state riorganizzate altrove — riordino, eliminazione o aggiunta,
		// magari da un'altra scheda dello stesso browser. Senza questo ricaricamento la
		// scheda resta sull'ordine vecchio, e il salvataggio automatico dei riquadri (il
		// debounce da 500ms qui sotto) rispedisce l'intero array con i `pageIndex` di prima,
		// annullando in silenzio l'operazione appena eseguita. Non serve un secondo
		// operatore: basta una scheda dimenticata aperta.
		"flyer.pages.reordered": () =>
		{
			fetchDataRef.current?.();
		},
		// Un campo manuale è stato cambiato altrove (editor card / selezione massiva in
		// FlyerManagePage): tiene il tag sulla gabbia coerente senza richiedere un refresh.
		"flyer.zone.manualOverrides.updated": (p) =>
		{
			if (p?.seriale == null)
				return;
			setZones(prev => prev.map(z => z.id === p.seriale
				? {...z, manualOverrides: p.manualOverrides, manualLabels: p.manualLabels}
				: z));
		},
		"flyer.variant.start": () =>
		{
			pendingAiStartRef.current = false;
			setAiProcessing(true);
			setOcrProgress(5);
		},
		"flyer.status.changed": (p) =>
		{
			setFlyerState(prev => prev ? {...prev, ...p} : p);
			if (p?.status === "PROCESSING" && !p?.isProcessingStale)
			{
				pendingAiStartRef.current = false;
				setAiProcessing(true);
				setOcrProgress(p?.processingProgress > 0 ? p.processingProgress : 5);
				return;
			}

			if (p?.status === "ERROR" || p?.status === "READY" || p?.status === "NEW" || p?.status === "DONE")
				pendingAiStartRef.current = false;
			setAiProcessing(false);
			if (p?.status === "ERROR" || p?.status === "READY" || p?.status === "NEW" || p?.status === "DONE")
				setOcrProgress(0);
		},
		"flyer.ocr.progress": (p) =>
		{
			setOcrProgress(p?.percent || 0);
		},
		"flyer.done": (p) =>
		{
			pendingAiStartRef.current = false;
			setAiProcessing(false);
			setProcessingSerials(new Set());
			setOcrProgress(100);
			// L'elaborazione ha appena creato/riscritto le card: i testi prodotto tenuti in
			// memoria per la ricerca sono ora vecchi. Si invalidano qui e non su ogni
			// "flyer.card.created" (uno per gabbia) per non scatenare una raffica di richieste;
			// se una ricerca è in corso, l'effetto legato alla query ricarica da solo.
			setCardTextBySerial(null);
			if (p?.skipped && p?.reason === "already-complete")
			{
				const completedCount = Number(p?.completedCount) || Number(p?.cards) || 0;
				message.info(
					completedCount > 0
						? `Nessuna nuova gabbia da elaborare: ${completedCount} card già complete. Usa "Rielabora tutto" per forzare un nuovo passaggio.`
						: `Nessuna nuova gabbia da elaborare. Usa "Rielabora tutto" per forzare un nuovo passaggio.`
				);
			}
			setTimeout(() => setOcrProgress(0), 3000);
		},
		"flyer.error": (p) =>
		{
			pendingAiStartRef.current = false;
			setAiProcessing(false);
			setProcessingSerials(new Set());
			setOcrProgress(0);
			message.error(p?.message || p?.detail || p?.reason || "Elaborazione AI interrotta");
		}
	});

	const isProcessingStale = flyerState?.status === "PROCESSING" && flyerState?.isProcessingStale;
	const canUnlockProcessing = isProcessingStale || flyerState?.status === "ERROR";

	const handleUnlockProcessing = useCallback(async () =>
	{
		if (!flyerId) return;

		setUnlockingProcessing(true);
		try
		{
			const {data} = await api.post(`/api/flyers/${flyerId}/processing/unlock`);
			const flyer = data?.flyer || null;
			if (flyer)
			{
				setFlyerState(flyer);
				syncFlyerProcessingUi(flyer);
			}
			message.success(flyerState?.status === "ERROR" ? "Errore sbloccato" : "Elaborazione AI sbloccata");
		}
		catch (error)
		{
			message.error(error?.response?.data?.error || "Impossibile sbloccare l'elaborazione");
		}
		finally
		{
			setUnlockingProcessing(false);
		}
	}, [flyerId, message, syncFlyerProcessingUi]);

	const handleZonesChange = useCallback((newZones) =>
	{
		setZones(newZones);
	}, []);

	// Prenota atomicamente `count` seriali promo-unici dal server.
	// Ritorna {status, serials}:
	//  - "ok"      → serials[] prenotati (univoci, sequenziali a livello promo)
	//  - "no-promo"→ volantino non in una promo: il chiamante usa il fallback locale
	//  - "error"   → rete/server KO: il chiamante DEVE annullare (niente stima locale,
	//                così i seriali non si accavallano tra postazioni)
	const reserveSerials = useCallback(async (count = 1) =>
	{
		const promoId = promoIdRef.current;
		if (!promoId)
			return {status: "no-promo", serials: []};
		try
		{
			const {data} = await api.post(`/api/promos/${promoId}/reserve-serials`, {count});
			if (data?.ok && Array.isArray(data.serials) && data.serials.length === count)
				return {status: "ok", serials: data.serials};
			return {status: "error", serials: []};
		}
		catch
		{
			return {status: "error", serials: []};
		}
	}, []);

	// 2. Carica effettivamente le immagini PDF convertite
	useEffect(() =>
	{
		// `pages` vuoto qui non distingue "volantino senza PDF" da "fetch non ancora risposta":
		// chiudere il loader in questo punto lo spegnerebbe anche al mount. Il caso "senza PDF"
		// è gestito in fetchData, a risposta ricevuta.
		if (!pages.length) return;

		let loadedCount = 0;
		const newImages = {};
		const apiUrl = import.meta.env.VITE_API_URL || "";

		pages.forEach((page) =>
		{
			const img = new Image();
			img.crossOrigin = "anonymous";
			const pageUrl = page.previewUrl || page.url;
			img.src = pageUrl.startsWith("http") ? pageUrl : `${apiUrl}${pageUrl}`;
			img.onload = () =>
			{
				newImages[page.index] = img;
				loadedCount++;
				if (loadedCount === pages.length)
				{
					setLoadedImages(newImages);
					setLoading(false);
				}
			};
			img.onerror = () =>
			{
				console.error("Errore caricamento immagine", page.url);
				loadedCount++;
				if (loadedCount === pages.length)
				{
					setLoadedImages(newImages);
					setLoading(false);
				}
			};
		});
	}, [pages]);

	// Il canvas lavora in coordinate PAGINA (full-res), le stesse in cui sono memorizzate le
	// gabbie: l'anteprima viene disegnata alla dimensione della pagina reale invece che alla
	// propria dimensione in pixel. Cosi' lo zoom in testata e' una percentuale della PAGINA
	// (indipendente dalla scala con cui e' stato rasterizzato il PDF) e ogni misura espressa
	// in coordinate scena — bordi, badge, maniglie del Transformer — ha lo stesso significato
	// su qualunque volantino. Stesso spazio adottato in VariantPreprocessPage, dove la
	// discrepanza era visibile perche' i canvas affiancati sono due.
	const totalPages = pages.length;
	const currentPage = pages[currentPageIndex];
	const currentImage = currentPage ? loadedImages[currentPage.index] : null;
	// La scala effettiva non si legge dal previewScale salvato a DB: si ricava dal rapporto
	// fra l'anteprima realmente caricata e la larghezza raster del volantino, perche' il
	// valore salvato puo' non corrispondere piu' ai file su disco (anteprime riscritte dopo
	// il caricamento del PDF) — la pagina sarebbe risultata larga il doppio e le gabbie
	// disegnate a meta' in alto a sinistra. Il previewScale del server resta il ripiego
	// (immagine non ancora caricata o riferimento assente).
	const effectivePreviewScale = useMemo(
		() => resolvePreviewScale(currentImage, rasterPageWidth, previewScale),
		[currentImage, rasterPageWidth, previewScale]
	);
	const toPreview = useCallback((value) => value, []);
	const toFull = useCallback((value) => value, []);
	const canGoPrev = currentPageIndex > 0;
	const canGoNext = currentPageIndex < totalPages - 1;
	// Dimensioni della pagina reale: l'anteprima e' rasterizzata alla scala effettiva (rapporto
	// fra immagine caricata e larghezza raster, vedi sopra), qui si torna sempre ai pixel pagina.
	const stageBaseWidth = (currentImage?.width || pageLayout.maxWidth || 1000) / effectivePreviewScale;
	const stageBaseHeight = (currentImage?.height || 1000) / effectivePreviewScale;
	const stageWidth = stageBaseWidth * scale + STAGE_MARGIN * 2;
	const stageHeight = stageBaseHeight * scale + STAGE_MARGIN * 2;
	const selectedIdsSet = useMemo(() => new Set(selectedIds), [selectedIds]);
	const primarySelectedId = selectedIds.length ? selectedIds[selectedIds.length - 1] : null;
	// Gabbie agganciate al riquadro di trasformazione: le selezionate della pagina a schermo,
	// escluse quelle in elaborazione AI (non si spostano né si ridimensionano).
	const transformerZoneIds = useMemo(
		() => zones
			.filter((zone) => zone.pageIndex === currentPageIndex && selectedIdsSet.has(zone.id) && !processingSerials.has(zone.id))
			.map((zone) => String(zone.id)),
		[zones, currentPageIndex, selectedIdsSet, processingSerials]
	);
	// Fine trascinamento/ridimensionamento sul canvas: con più gabbie nel riquadro Konva notifica
	// ciascuna separatamente, nello stesso gesto. Le geometrie si raccolgono e si applicano dopo
	// l'ultima notifica, in un solo aggiornamento: un solo passo di Annulla, e il vincolo dentro
	// la pagina vale per il gruppo intero (le gabbie non si schiacciano contro il bordo).
	const queueZoneGeometry = useCallback((zoneId, geometry) =>
	{
		const pending = pendingGeometryRef.current;
		if (!pending.size)
		{
			queueMicrotask(() =>
			{
				const updates = new Map(pendingGeometryRef.current);
				pendingGeometryRef.current.clear();
				setZones((prev) => applyZoneGeometryUpdates(prev, updates, stageBaseWidth, stageBaseHeight));
			});
		}
		pending.set(zoneId, geometry);
	}, [stageBaseWidth, stageBaseHeight]);
	const apiBaseUrl = import.meta.env.VITE_API_URL || "";
	const zonesByPage = useMemo(() =>
	{
		const map = new Map();
		zones.forEach((zone) =>
		{
			map.set(zone.pageIndex, (map.get(zone.pageIndex) || 0) + 1);
		});
		return map;
	}, [zones]);
	// Carica una volta sola le descrizioni prodotto (dalle FlyerCard) per la ricerca.
	const loadCardTexts = useCallback(async () =>
	{
		if (cardTextBySerial || cardTextFetchRef.current || !flyerId) return;
		cardTextFetchRef.current = true;
		setCardTextLoading(true);
		try
		{
			const {data} = await api.get(`/api/flyers/${flyerId}/cards`);
			const map = new Map();
			for (const card of (Array.isArray(data) ? data : []))
			{
				if (card?.seriale == null) continue;
				const label = String(card.ai?.descrizione || card.ai?.nome_prodotto || "").trim();
				const haystack = [label, card.ai?.marca, card.ai?.nome_prodotto]
					.filter(Boolean).join(" ").toLowerCase();
				map.set(Number(card.seriale), {label, haystack});
			}
			setCardTextBySerial(map);
		}
		catch
		{
			// Ricerca comunque utilizzabile per seriale e valori manuali: mappa vuota, niente errore a video.
			setCardTextBySerial(new Map());
		}
		finally
		{
			cardTextFetchRef.current = false;
			setCardTextLoading(false);
		}
	}, [cardTextBySerial, flyerId]);

	// La pagina NON fa remount al cambio di :flyerId (stessa route, solo il parametro cambia),
	// quindi la cache dei testi prodotto sopravviverebbe al passaggio a un altro volantino e
	// mostrerebbe le descrizioni di quello precedente. I seriali sono univoci per PROMO, non
	// globalmente: fra promo diverse lo stesso numero esiste su prodotti diversi, quindi non
	// sarebbe un semplice "nessun risultato" ma un nome sbagliato accanto al seriale giusto.
	useEffect(() =>
	{
		cardTextFetchRef.current = false;
		setCardTextBySerial(null);
		setZoneQuery("");
	}, [flyerId]);

	// Il caricamento è agganciato alla presenza di una query, non al focus del campo: così
	// vale anche per un incolla o per un valore impostato senza passare da un focus reale.
	useEffect(() =>
	{
		if (zoneQuery.trim()) loadCardTexts();
	}, [zoneQuery, loadCardTexts]);

	// Le zone del volantino che soddisfano la query, ordinate per pagina. `null` = nessuna
	// ricerca in corso (la sidebar mostra l'accordion normale).
	const zoneSearchResults = useMemo(() =>
	{
		const query = zoneQuery.trim().toLowerCase();
		if (!query) return null;
		const bareNumber = query.replace(/^#/, "");
		return zones
			.filter((zone) =>
			{
				if (bareNumber && String(zone.id).includes(bareNumber)) return true;
				if (cardTextBySerial?.get(zone.id)?.haystack.includes(query)) return true;
				const labels = zone.manualLabels && typeof zone.manualLabels === "object"
					? Object.values(zone.manualLabels)
					: [];
				return labels.some((value) => String(Array.isArray(value) ? value.join(" ") : (value ?? ""))
					.toLowerCase().includes(query));
			})
			.sort((a, b) => a.pageIndex - b.pageIndex || a.id - b.id);
	}, [zoneQuery, zones, cardTextBySerial]);

	// Gabbie trovate dalla ricerca, evidenziate sul canvas (null = nessuna ricerca attiva).
	const searchMatchIds = useMemo(
		() => (zoneSearchResults ? new Set(zoneSearchResults.map(z => z.id)) : null),
		[zoneSearchResults]
	);

	// La vista segue il primo risultato: se sta sulla pagina a video ci si scrolla sopra, altrimenti
	// si cambia pagina e lo scroll parte a immagine caricata (pendingFocusZoneRef). Senza, la gabbia
	// gialla potrebbe cadere fuori dalla parte visibile o su una pagina non guardata. Scatta solo
	// al cambio dei risultati, così sfogliare a mano durante una ricerca resta libero.
	useEffect(() =>
	{
		if (!zoneSearchResults?.length) return;
		const onPage = zoneSearchResults.find(z => z.pageIndex === currentPageIndex);
		if (onPage)
		{
			scrollZoneIntoView(onPage);
			return;
		}
		const first = zoneSearchResults[0];
		// changePage azzera pendingFocusZoneRef e sospende l'auto-follow della selezione, che
		// altrimenti riporterebbe subito la vista indietro: il fuoco si imposta DOPO.
		changePage(first.pageIndex);
		pendingFocusZoneRef.current = first.id;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [zoneSearchResults]);

	const groupedZones = useMemo(() =>
	{
		const pageGroups = pages.map((page, pageIndex) => ({
			pageIndex,
			page,
			zones: []
		}));

		zones.forEach((zone) =>
		{
			if (!pageGroups[zone.pageIndex])
			{
				pageGroups[zone.pageIndex] = {
					pageIndex: zone.pageIndex,
					page: pages[zone.pageIndex],
					zones: []
				};
			}
			pageGroups[zone.pageIndex].zones.push(zone);
		});

		return pageGroups.filter((group) => group.zones.length > 0);
	}, [pages, zones]);
	const activeZonePanelKey = useMemo(() =>
	{
		if (!groupedZones.length) return undefined;
		const currentPageKey = String(currentPageIndex);
		const hasCurrentPageGroup = groupedZones.some((group) => String(group.pageIndex) === currentPageKey);
		// Nessun fallback al primo gruppo: se la pagina corrente non ha ancora gabbie, il pannello
		// resta chiuso invece di mostrare quello di un'altra pagina. Il fallback esisteva ma
		// causava uno scatto visibile nella sidebar (chiusura del gruppo mostrato + apertura di
		// quello corretto) proprio nel momento in cui si disegna la prima gabbia sulla pagina
		// corrente, perché la pagina "compariva" tra i gruppi e activeZonePanelKey cambiava di colpo.
		return hasCurrentPageGroup ? currentPageKey : undefined;
	}, [currentPageIndex, groupedZones]);
	const changePage = useCallback((nextIndex) =>
	{
		pendingFocusZoneRef.current = null;
		// Azzera la selezione solo se la pagina cambia davvero (l'accordion "Gabbie tracciate"
		// richiama changePage anche solo riaprendo/richiudendo il pannello della pagina già
		// corrente) E la selezione corrente è scoped a una sola pagina. Una selezione che
		// copre già più pagine (checkbox "seleziona tutte" globale) va invece preservata
		// mentre si naviga tra le pagine, altrimenti basterebbe guardare un'altra pagina per
		// perdere la selezione appena fatta per un'azione bulk (es. delete multiplo con conferma).
		// suppressAutoPageRef evita in entrambi i casi che l'effetto di auto-follow pagina
		// scavalchi la navigazione manuale seguendo l'ultima zona selezionata (che potrebbe
		// stare su una pagina diversa da quella appena aperta).
		if (nextIndex !== currentPageIndex)
		{
			const selectedPageIndexes = new Set(
				zones.filter((zone) => selectedIdsSet.has(zone.id)).map((zone) => zone.pageIndex)
			);
			const isCrossPageSelection = selectedPageIndexes.size > 1;
			suppressAutoPageRef.current = true;
			if (!isCrossPageSelection)
			{
				setSelectedIds([]);
			}
		}
		setCurrentPageIndex(nextIndex);
	}, [currentPageIndex, zones, selectedIdsSet]);

	const scrollZoneIntoView = useCallback((zone, behavior = "smooth") =>
	{
		if (!zone || zone.pageIndex !== currentPageIndex) return false;

		const scrollContainer = scrollContainerRef.current;
		const stageContainer = stageRef.current?.container?.();
		if (!scrollContainer || !stageContainer) return false;

		const containerRect = scrollContainer.getBoundingClientRect();
		const stageRect = stageContainer.getBoundingClientRect();
		// zone.x/y/w/h sono in coordinate pagina, come lo spazio scena: basta lo zoom.
		const zoomFactor = scale;
		const zoneLeft = scrollContainer.scrollLeft
			+ (stageRect.left - containerRect.left)
			+ STAGE_MARGIN
			+ (zone.x * zoomFactor);
		const zoneTop = scrollContainer.scrollTop
			+ (stageRect.top - containerRect.top)
			+ STAGE_MARGIN
			+ (zone.y * zoomFactor);
		const zoneWidth = Math.max(zone.w * zoomFactor, 1);
		const zoneHeight = Math.max(zone.h * zoomFactor, 1);
		const maxScrollLeft = Math.max(scrollContainer.scrollWidth - scrollContainer.clientWidth, 0);
		const maxScrollTop = Math.max(scrollContainer.scrollHeight - scrollContainer.clientHeight, 0);
		const targetLeft = clamp(
			zoneLeft - ((scrollContainer.clientWidth - zoneWidth) / 2),
			0,
			maxScrollLeft
		);
		const targetTop = clamp(
			zoneTop - ((scrollContainer.clientHeight - zoneHeight) / 2),
			0,
			maxScrollTop
		);

		scrollContainer.scrollTo({
			left: targetLeft,
			top: targetTop,
			behavior
		});

		return true;
	}, [currentPageIndex, scale]);

	// 3. Calcola il layout verticale una volta caricate le immagini
	useEffect(() =>
	{
		if (loading || Object.keys(loadedImages).length === 0) return;

		let currentY = 0;
		let maxWidth = 0;
		const offsets = [];

		for (let i = 0; i < pages.length; i++)
		{
			const img = loadedImages[i];
			offsets[i] = currentY;
			if (img)
			{
				currentY += img.height + PAGE_SPACING;
				maxWidth = Math.max(maxWidth, img.width);
			}
			else
			{
				currentY += 1000 + PAGE_SPACING; // Default height if image missing
			}
		}

		setPageLayout({offsets, totalHeight: currentY, maxWidth});
	}, [loadedImages, loading, pages.length]);

	// Gestione pagine (riordino, spostamento, eliminazione, aggiunta): logica condivisa con
	// la split-view variante. `pagesBusy` sospende l'autosalvataggio qui sotto.
	const handleAfterPageDelete = useCallback(
		() => setCurrentPageIndex((prev) => Math.max(0, Math.min(prev, pages.length - 2))),
		[pages.length]
	);

	const {
		pagesBusy,
		pagesBusyRef,
		addPagesOpen,
		openAddPages,
		closeAddPages,
		handlePagesDragEnd,
		handlePageMenuAction,
		handleAddPagesCompleted
	} = usePageManagement({
		flyerId,
		pages,
		refetch: fetchData,
		onAfterDelete: handleAfterPageDelete
	});

	const savePreprocessData = useCallback(async (zonesToSave, configToSave) =>
	{
		setSaving(true);
		try
		{
			const {data} = await api.patch(`/api/flyers/${flyerId}/preprocess-zones`, {
				zones: zonesToSave,
				config: configToSave
			});

			// Il server normalizza la geometria e può riportare dentro pagina una gabbia nata
			// fuori (volantini precedenti al vincolo lato client). Senza adottare la risposta il
			// canvas continuerebbe a mostrare le coordinate vecchie fino a un ricaricamento,
			// divergendo in silenzio dal DB. Si adotta solo se nel frattempo l'utente non ha
			// toccato altro — la firma dell'inviato deve coincidere ancora con lo stato corrente,
			// altrimenti si sovrascriverebbero modifiche più recenti (e comunque il prossimo
			// autosave ripeterà la normalizzazione).
			const serverZones = Array.isArray(data?.zones) ? data.zones : null;
			const sentSig = structuralZonesSig(zonesToSave);
			if (serverZones
				&& structuralZonesSig(serverZones) !== sentSig
				&& structuralZonesSig(zonesRef.current) === sentSig)
			{
				// Correzione del server, non azione dell'utente: fuori dallo storico Annulla,
				// altrimenti un Ctrl+Z rimetterebbe la gabbia fuori pagina e l'autosave la
				// riporterebbe dentro, in un rimpallo senza fine.
				restoringRef.current = true;
				setZones(serverZones);
			}
		}
		catch (err)
		{
			message.error("Errore salvataggio dati");
		}
		finally
		{
			setSaving(true); // piccolo delay visivo
			setTimeout(() => setSaving(false), 300);
		}
	}, [flyerId, message]);

	// Ref sempre allineata all'ultima savePreprocessData: il timer di autosave la invoca senza
	// tenerla fra le proprie dipendenze (altrimenti riarmerebbe il debounce ad ogni render).
	const savePreprocessDataRef = useRef(savePreprocessData);
	savePreprocessDataRef.current = savePreprocessData;

	// Specchio dell'ultimo `zones` renderizzato: quando torna la risposta del salvataggio serve
	// sapere se nel frattempo l'utente ha toccato altro, e la closure di savePreprocessData
	// (memoizzata su flyerId) vedrebbe un valore stantio.
	zonesRef.current = zones;

	useEffect(() =>
	{
		if (loading || loadedFlyerIdRef.current !== flyerId) return;
		// Operazione sulle pagine in corso: il server sta riscrivendo l'intero array dei
		// riquadri rimappandone gli indici di pagina, e questo salvataggio rimetterebbe quelli
		// vecchi. Il flag è in dipendenza (il cleanup annulla il timer già in coda) ED è
		// ricontrollato allo scatto, perché un timer armato un istante prima dell'operazione
		// non passa dal cleanup.
		if (pagesBusy) return;
		// Il timer vive dentro l'effetto: il cleanup lo annulla al cambio di :flyerId (la pagina non
		// fa remount) e allo smontaggio. Senza questo, un salvataggio pendente scriverebbe le zone
		// del volantino precedente su quello appena aperto, o partirebbe a pagina già lasciata.
		const timeout = setTimeout(
			() =>
			{
				if (pagesBusyRef.current) return;
				// Niente previewScale nel payload: la scala è una proprietà dei file di anteprima su
				// disco, decisa al caricamento del PDF. Riscriverla da qui rischiava di disallineare
				// il valore salvato dai file realmente presenti (scheda aperta col valore vecchio).
				savePreprocessDataRef.current(zones, {rectStrokeWidth, rectOpacity});
			},
			500
		);
		return () => clearTimeout(timeout);
	}, [zones, rectStrokeWidth, rectOpacity, loading, flyerId, pagesBusy, pagesBusyRef]);

	// Registra lo storico per Annulla/Rifai: impila lo stato PRECEDENTE ogni volta che la struttura
	// delle zone cambia. Ignora il caricamento iniziale e i ripristini undo/redo (restoringRef).
	useEffect(() =>
	{
		if (loading)
		{
			prevZonesRef.current = zones;
			return;
		}
		if (restoringRef.current)
		{
			restoringRef.current = false;
			prevZonesRef.current = zones;
			return;
		}
		// Movimento intermedio di una sequenza di frecce: avanza la baseline SENZA registrare
		// (il passo di undo è già stato impilato all'inizio della sequenza).
		if (suppressHistoryRef.current)
		{
			suppressHistoryRef.current = false;
			prevZonesRef.current = zones;
			return;
		}
		if (structuralZonesSig(prevZonesRef.current) !== structuralZonesSig(zones))
		{
			undoStackRef.current.push(prevZonesRef.current);
			if (undoStackRef.current.length > 50)
				undoStackRef.current.shift();
			// Nuova azione dell'utente → i redo accumulati non sono più coerenti: svuota la pila.
			redoStackRef.current = [];
			// Un'azione non-freccia interrompe l'eventuale sequenza di frecce in corso.
			arrowBurstRef.current = false;
			if (arrowTimerRef.current) clearTimeout(arrowTimerRef.current);
		}
		prevZonesRef.current = zones;
	}, [zones, loading]);

	// Pulizia del timer di coalescing frecce allo smontaggio (non nell'effetto tastiera, che
	// si ri-registra ad ogni cambio zone e cancellerebbe il timer durante la sequenza).
	useEffect(() => () => { if (arrowTimerRef.current) clearTimeout(arrowTimerRef.current); }, []);

	useEffect(() =>
	{
		if (suppressAutoPageRef.current)
		{
			suppressAutoPageRef.current = false;
			return;
		}
		if (primarySelectedId)
		{
			const zone = zones.find(z => z.id === primarySelectedId);
			if (!zone) return;
			if (currentPageIndex !== zone.pageIndex)
			{
				setCurrentPageIndex(zone.pageIndex);
			}
		}
	}, [primarySelectedId, zones, currentPageIndex]);

	useEffect(() =>
	{
		if (!scrollContainerRef.current) return;
		if (pendingFocusZoneRef.current !== null) return;
		scrollContainerRef.current.scrollTo({top: 0, behavior: "auto"});
	}, [currentPageIndex]);

	useEffect(() =>
	{
		const pendingZoneId = pendingFocusZoneRef.current;
		if (pendingZoneId === null) return;

		const zone = zones.find((item) => item.id === pendingZoneId);
		if (!zone)
		{
			pendingFocusZoneRef.current = null;
			return;
		}
		if (zone.pageIndex !== currentPageIndex || !currentImage) return;

		const frameId = requestAnimationFrame(() =>
		{
			const didScroll = scrollZoneIntoView(zone);
			if (didScroll && pendingFocusZoneRef.current === pendingZoneId)
				pendingFocusZoneRef.current = null;
		});

		return () => cancelAnimationFrame(frameId);
	}, [currentImage, currentPageIndex, scrollZoneIntoView, zones]);

	const handlePageDragEnd = useCallback((pageIndex, event) =>
	{
		const {active, over} = event;
		if (!over || active.id === over.id) return;

		setZones((items) => reorderZonesWithinPage(items, pageIndex, active.id, over.id).map((item) =>
		{
			if (item.pageIndex !== pageIndex || item.type === "banner")
				return item;

			return {
				...item,
				status: "reprocess"
			};
		}));
	}, []);

	const handleZonePanelChange = useCallback((key) =>
	{
		const nextKey = Array.isArray(key) ? key[0] : key;
		if (typeof nextKey !== "string") return;

		changePage(Number(nextKey));
	}, [changePage]);

	// Gestione disegno
	const getPointerPos = useCallback(() =>
	{
		const stage = stageRef.current;
		if (!stage) return null;
		const pos = stage.getPointerPosition();
		if (!pos) return null;
		// Lo Stage è traslato di STAGE_MARGIN (px schermo) per centrare la pagina:
		// scalo il puntatore allo stesso sistema di coordinate della scena.
		return {
			x: (pos.x - STAGE_MARGIN) / scale,
			y: (pos.y - STAGE_MARGIN) / scale
		};
	}, [scale]);

	const trackPointerPosition = useCallback(() =>
	{
		const pos = getPointerPos();
		if (!pos) return null;

		lastPointerPosRef.current = {
			...pos,
			pageIndex: currentPageIndex
		};

		return pos;
	}, [currentPageIndex, getPointerPos]);

	const handleMouseDown = (e) =>
	{
		trackPointerPosition();
		const clickedOnEmpty = e.target === stageRef.current || e.target.attrs?.name === "page-image";
		if (!clickedOnEmpty) return;

		if (hasSelectionModifier(e))
		{
			// Cmd/Ctrl/Shift + trascina su area vuota → selezione multipla a rettangolo.
			// Prima qui si usciva senza fare nulla (né disegnava né selezionava): il gesto,
			// comune in Figma/Illustrator, sembrava non funzionare affatto.
			isMarqueeSelecting.current = true;
			startPos.current = getPointerPos();
			return;
		}

		setSelectedIds([]);
		isDrawing.current = true;
		startPos.current = getPointerPos();
	};

	const handleMouseMove = (e) =>
	{
		const currentPos = trackPointerPosition();

		if (isMarqueeSelecting.current)
		{
			if (!currentPos || !startPos.current) return;
			updatePreviewRect({
				x: Math.min(startPos.current.x, currentPos.x),
				y: Math.min(startPos.current.y, currentPos.y),
				width: Math.abs(currentPos.x - startPos.current.x),
				height: Math.abs(currentPos.y - startPos.current.y)
			}, marqueeStyle);
			return;
		}

		if (!isDrawing.current) return;
		if (!currentPos || !startPos.current) return;

		if (perfEnabledRef.current)
		{
			drawStartRef.current = performance.now();
		}
		updatePreviewRect({
			x: Math.min(startPos.current.x, currentPos.x),
			y: Math.min(startPos.current.y, currentPos.y),
			width: Math.abs(currentPos.x - startPos.current.x),
			height: Math.abs(currentPos.y - startPos.current.y)
		});
	};

	const handleMouseUp = async (e) =>
	{
		trackPointerPosition();

		if (isMarqueeSelecting.current)
		{
			isMarqueeSelecting.current = false;
			hidePreviewRect();
			const endPos = getPointerPos();
			const start = startPos.current;
			startPos.current = null;
			if (!endPos || !start) return;

			const w = Math.abs(endPos.x - start.x);
			const h = Math.abs(endPos.y - start.y);
			if (w <= 3 || h <= 3) return; // trascinamento troppo piccolo: non è un vero marquee

			// Rettangolo di selezione riportato in coordinate PAGINA (le stesse di zone.x/y/w/h),
			// stessa conversione già usata per il disegno di una nuova gabbia qui sotto.
			const selLeft = toFull(Math.min(start.x, endPos.x));
			const selTop = toFull(Math.min(start.y, endPos.y));
			const selRight = toFull(Math.max(start.x, endPos.x));
			const selBottom = toFull(Math.max(start.y, endPos.y));

			// Intersezione rettangolo-rettangolo (non serve contenimento totale): qualunque
			// gabbia anche solo sfiorata dal rettangolo entra in selezione.
			const hitIds = zones
				.filter((z) => z.pageIndex === currentPageIndex)
				.filter((z) => z.x < selRight && z.x + z.w > selLeft && z.y < selBottom && z.y + z.h > selTop)
				.map((z) => z.id);

			if (hitIds.length)
				setSelectedIds((prev) => [...new Set([...prev, ...hitIds])]);
			return;
		}

		if (!isDrawing.current) return;
		isDrawing.current = false;
		hidePreviewRect();

		const endPos = getPointerPos();
		const start = startPos.current;
		startPos.current = null;
		if (!endPos || !start) return;

		const w = Math.abs(endPos.x - start.x);
		const h = Math.abs(endPos.y - start.y);

		if (w > 5 && h > 5)
		{ // Ridotta tolleranza per facilitare disegno
			const globalX = Math.min(start.x, endPos.x);
			const globalY = Math.min(start.y, endPos.y);

			const pageIndex = currentPageIndex;
			const localY = globalY;
			const fullX = toFull(globalX);
			const fullY = toFull(localY);
			const fullW = toFull(w);
			const fullH = toFull(h);

			// Seriale prenotato atomicamente dal server (univoco a livello promo).
			const reserved = await reserveSerials(1);
			let newId;
			if (reserved.status === "ok")
				newId = reserved.serials[0];
			else if (reserved.status === "no-promo")
				newId = zones.reduce((max, z) => Math.max(max, z.id), 0) + 1; // fallback volantino senza promo
			else
			{
				message.error({content: "Impossibile assegnare il seriale: rete non disponibile. Riprova.", key: "reserve-serial"});
				return;
			}

			// Vincola la nuova gabbia dentro i bordi pagina (nessuno sconfinamento nel margine canvas).
			const newZone = clampZoneToPage({
				id: newId,
				pageIndex,
				type: activeType,
				x: fullX,
				y: fullY,
				w: fullW,
				h: fullH
			}, stageBaseWidth, stageBaseHeight);

			setZones((prev) => [...prev, newZone]);
			setSelectedIds([newId]);
		}
	};

	const handleZoneSelection = useCallback((zoneId, pageIndex, event, options = {}) =>
	{
		// Konva genera comunque un evento click/tap sul rettangolo per QUALSIASI pulsante del
		// mouse (rileva mousedown+mouseup sullo stesso nodo, senza guardare quale pulsante) —
		// quindi un tasto destro su una gabbia già parte di una multi-selezione, senza tasti
		// modificatori premuti, ricadeva nel ramo "sostituisci selezione" e la collassava a quella
		// sola gabbia. Il menu contestuale (handleZoneContextMenu) restava corretto perché il
		// conteggio lo congela in zoneMenu.targetIds all'apertura, ma la selezione live su
		// canvas/sidebar risultava quella appena ridotta a 1 elemento.
		if ((event?.evt || event)?.button === 2)
			return;

		const shouldToggle = hasSelectionModifier(event);
		const isRemovingSelection = shouldToggle && selectedIdsSet.has(zoneId);
		const shouldFocus = options.source === "sidebar" && !isRemovingSelection;

		if (shouldFocus)
			pendingFocusZoneRef.current = zoneId;

		setSelectedIds((prev) =>
		{
			if (!shouldToggle)
				return [zoneId];

			if (prev.includes(zoneId))
				return prev.filter((id) => id !== zoneId);

			return [...prev, zoneId];
		});

		if (!isRemovingSelection && currentPageIndex !== pageIndex)
		{
			setCurrentPageIndex(pageIndex);
			return;
		}

		if (!shouldFocus) return;

		const zone = zones.find((item) => item.id === zoneId);
		if (!zone)
		{
			pendingFocusZoneRef.current = null;
			return;
		}

		requestAnimationFrame(() =>
		{
			const didScroll = scrollZoneIntoView(zone);
			if (didScroll && pendingFocusZoneRef.current === zoneId)
				pendingFocusZoneRef.current = null;
		});
	}, [currentPageIndex, scrollZoneIntoView, selectedIdsSet, zones]);

	// Seleziona/deseleziona in blocco tutte le gabbie di una pagina (checkbox nell'header del
	// pannello "Gabbie tracciate"): si somma alla selezione esistente invece di sostituirla,
	// così spuntare più pagine accumula una selezione multi-pagina (coerente con la checkbox
	// globale, che aggrega tutte le pagine insieme); se sono già tutte selezionate, il click
	// toglie solo le gabbie di QUESTA pagina, lasciando intatta la selezione delle altre.
	const handleSelectAllZonesOnPage = useCallback((group, allSelected, event) =>
	{
		event?.stopPropagation();
		if (!group?.zones?.length) return;
		const pageZoneIds = group.zones.map((zone) => zone.id);
		if (allSelected)
		{
			const pageZoneIdsSet = new Set(pageZoneIds);
			setSelectedIds((prev) => prev.filter((id) => !pageZoneIdsSet.has(id)));
			return;
		}
		setSelectedIds((prev) => Array.from(new Set([...prev, ...pageZoneIds])));
		if (currentPageIndex !== group.pageIndex)
			setCurrentPageIndex(group.pageIndex);
	}, [currentPageIndex]);

	// Seleziona/deseleziona in blocco TUTTE le gabbie del volantino, su ogni pagina (checkbox
	// accanto al titolo "Gabbie tracciate"). A differenza della checkbox per-pagina non cambia
	// pagina corrente: le zone selezionate finiscono su pagine diverse, non ce n'è una sola su
	// cui spostarsi. suppressAutoPageRef evita che l'effetto di auto-follow pagina reagisca
	// all'ultima zona dell'array (potrebbe stare su una pagina qualunque).
	const handleSelectAllZonesGlobal = useCallback((allSelected, event) =>
	{
		event?.stopPropagation();
		if (!zones.length) return;
		suppressAutoPageRef.current = true;
		setSelectedIds(allSelected ? [] : zones.map((zone) => zone.id));
	}, [zones]);

	// Tasto destro su una zona: apre il menu contestuale (rielaborazione AI + campi manuali per
	// le gabbie prodotto, oppure i soli campi banner per le gabbie banner).
	const handleZoneContextMenu = useCallback((zoneId, e) =>
	{
		const evt = e?.evt || e;
		evt?.preventDefault?.();
		const zone = zones.find(z => z.id === zoneId);
		if (!zone) return;

		const isBannerZone = zone.type === "banner";
		if (!isBannerZone && zone.type !== "gabbia") return; // tipo zona non gestito dal menu

		// Se il tasto destro cade su una zona già parte di una multi-selezione, il bersaglio
		// è l'intera selezione (stesso tipo); altrimenti la singola zona (e vi allinea la selezione).
		const inSelection = selectedIdsSet.has(zoneId) && selectedIds.length > 1;
		const targetIds = inSelection
			? zones.filter(z => selectedIdsSet.has(z.id) && z.type === zone.type).map(z => z.id)
			: [zoneId];
		if (!inSelection) setSelectedIds([zoneId]);

		// Bersaglio della rielaborazione AI: SEMPRE le sole gabbie dell'intera selezione, non
		// filtrate per il tipo su cui è caduto il tasto destro come targetIds qui sopra. Senza
		// questa distinzione, selezionando gabbie+banner insieme e cliccando col destro su un
		// banner, "Rielabora" spariva del tutto anche se la selezione conteneva gabbie da
		// rielaborare — i banner non passano comunque mai dall'AI.
		const reprocessTargetIds = inSelection
			? zones.filter(z => selectedIdsSet.has(z.id) && z.type === "gabbia").map(z => z.id)
			: (isBannerZone ? [] : [zoneId]);

		setZoneMenu({
			x: evt.clientX, y: evt.clientY,
			targetIds, reprocessTargetIds,
			kind: isBannerZone ? "banner" : "gabbia"
		});
	}, [zones, selectedIds, selectedIdsSet]);

	// Apre la modale unificata dei campi manuali per il set di campi del tipo di zona (gabbia
	// prodotto o banner); precompila ogni campo se le gabbie bersaglio condividono lo stesso valore.
	// Sulle gabbie banner il valore viene dalla card (vedi readBannerZoneValue).
	const openManualFields = useCallback(async (targetIds, kind) =>
	{
		const fieldList = kind === "banner" ? bannerMenuFields : manualFields;
		if (!Array.isArray(targetIds) || !targetIds.length || !fieldList.length)
			return;

		const findZone = (id) => zones.find(z => z.id === id);
		let readValue = (id, key) => findZone(id)?.manualOverrides?.[key];
		if (kind === "banner")
		{
			const bannerBySerial = await fetchBannerValuesBySerial(flyerId);
			readValue = (id, key) => readBannerZoneValue(findZone(id), bannerBySerial, key);
		}

		const initialValues = collectSharedInitialValues(fieldList, targetIds, readValue);
		setManualModal({fields: fieldList, targetIds, initialValues});
	}, [manualFields, bannerMenuFields, zones, flyerId]);

	// Applica i campi modificati nella modale alle gabbie bersaglio in una sola chiamata batch:
	// persiste sul backend e aggiorna lo stato locale delle zone con i campi che il backend ha
	// davvero salvato. In caso di errore la modale resta aperta, così il valore non va perso.
	const handleApplyManual = useCallback(async (fieldsPayload) =>
	{
		const current = manualModal;
		if (!current || !Array.isArray(fieldsPayload) || !fieldsPayload.length)
			return;
		const {targetIds} = current;

		try
		{
			const {data} = await api.put(`/api/flyers/${flyerId}/zone-manual-fields`, {
				zoneIds: targetIds,
				fields: fieldsPayload
			});
			if (data?.ok === false)
			{
				message.error(data.error || "Errore durante l'applicazione");
				return;
			}

			const savedFields = Array.isArray(data?.fields) ? data.fields : fieldsPayload;
			const labelByKey = new Map(fieldsPayload.map(({fieldKey, label}) => [fieldKey, label]));
			setZones(prev => prev.map(z => (targetIds.includes(z.id)
				? applyManualFieldsToZone(z, savedFields, labelByKey)
				: z)));

			message.success(`Campi applicati a ${targetIds.length} gabbia/e`);
			setManualModal(null);
		}
		catch (err)
		{
			message.error(err?.response?.data?.error || "Errore durante l'applicazione");
		}
	}, [manualModal, flyerId, message]);

	const updateZone = (updatedZone) =>
	{
		const previousZone = zones.find(z => z.id === updatedZone.id);
		const nextZone = {
			...updatedZone,
			type: updatedZone.type === "banner" ? "banner" : "gabbia",
			status: updatedZone.type === "banner"
				? (updatedZone.status || previousZone?.status || "new")
				: (previousZone ? "reprocess" : (updatedZone.status || "new"))
		};
		const newZones = zones.map(z => z.id === updatedZone.id ? nextZone : z);
		handleZonesChange(newZones);
	};

	const handleAiProcessing = useCallback(async ({forceReprocess = false, serials = null} = {}) =>
	{
		if (!flyerId) return message.warning("FlyerId non disponibile");
		if (!zones.length) return message.warning("Nessuna gabbia tracciata");
		if (!pages.length) return message.warning("Pagine non disponibili");
		if (aiProcessing) return;

		// Limita i serials alle sole gabbie valide: i banner non richiedono AI.
		const gabbiaIds = new Set(zones.filter(z => z.type === "gabbia").map(z => z.id));
		const cleanSerials = Array.isArray(serials)
			? [...new Set(serials)].filter(id => gabbiaIds.has(id))
			: null;
		if (Array.isArray(serials) && cleanSerials.length === 0)
			return message.warning("Nessuna gabbia valida selezionata");

		// Stato visivo ottimistico: i seriali bersaglio (o tutte le gabbie se elaborazione intera).
		setProcessingSerials(new Set(cleanSerials ?? [...gabbiaIds]));

		pendingAiStartRef.current = true;
		setAiProcessing(true);
		if (ocrProgress === 0) setOcrProgress(5);
		try
		{
			const payload = {flyerId, forceReprocess};
			if (cleanSerials) payload.serials = cleanSerials;
			const {data} = await api.post("/api/ai/processing-preprocess", payload);
			if (data?.jobId)
			{
				setFlyerState(prev => prev
					? {...prev, status: "PROCESSING", processingJobId: data.jobId, isProcessingStale: false}
					: {status: "PROCESSING", processingJobId: data.jobId, isProcessingStale: false});
			}

			let flyer = null;
			try
			{
				flyer = await loadFlyerState();
			}
			catch (refreshError)
			{
				console.error("Impossibile riallineare lo stato flyer dopo l'avvio AI", refreshError);
			}

			if (!flyer || (flyer.status === "PROCESSING" && !flyer.isProcessingStale))
				message.success(forceReprocess ? "Rielaborazione AI avviata" : "Elaborazione AI avviata");
		}
		catch (err)
		{
			pendingAiStartRef.current = false;
			setProcessingSerials(new Set());
			message.error(err?.response?.data?.error || "Errore durante l'elaborazione AI.");
			setAiProcessing(false);
			setOcrProgress(0);
		}
	}, [aiProcessing, flyerId, loadFlyerState, message, ocrProgress, pages.length, zones]);

	// Shortcut Delete / Copy / Paste
	useEffect(() =>
	{
		const handleKeyDown = (e) =>
		{
			if (isEditableTarget(e.target)) return;

			const normalizedKey = String(e.key || "").toLowerCase();
			if ((normalizedKey === "delete" || normalizedKey === "backspace") && selectedIds.length)
			{
				e.preventDefault();
				const idsToDelete = selectedIdsSet;
				const deleteSelected = () =>
				{
					handleZonesChange(zones.filter((zone) => !idsToDelete.has(zone.id)));
					setSelectedIds([]);
				};
				// Sotto conferma solo l'eliminazione multipla: il cestino sulla singola gabbia
				// nella sidebar resta un'azione diretta, qui il rischio è cancellare in un colpo
				// solo (es. dopo "seleziona tutte") decine di gabbie senza modo di tornare indietro.
				if (selectedIds.length > 1)
				{
					modal.confirm({
						title: `Eliminare ${selectedIds.length} gabbie selezionate?`,
						content: "Questa azione non può essere annullata.",
						okText: "Elimina",
						cancelText: "Annulla",
						okButtonProps: {danger: true},
						onOk: deleteSelected
					});
					return;
				}
				deleteSelected();
				return;
			}

			// Pressioni consecutive dello stesso tipo (frecce, oppure scala) = un solo passo di
			// undo: al primo tasto della sequenza impila lo stato pre-sequenza, poi sopprime la
			// registrazione dei passi successivi. Chiamarla solo se lo stato cambierà davvero
			// (l'effetto dello storico deve vedere un nuovo array per consumare la soppressione).
			const beginKeyboardBurst = (kind) =>
			{
				if (!arrowBurstRef.current || burstKindRef.current !== kind)
				{
					undoStackRef.current.push(prevZonesRef.current);
					if (undoStackRef.current.length > 50) undoStackRef.current.shift();
					redoStackRef.current = [];
					arrowBurstRef.current = true;
					burstKindRef.current = kind;
				}
				if (arrowTimerRef.current) clearTimeout(arrowTimerRef.current);
				arrowTimerRef.current = setTimeout(() => { arrowBurstRef.current = false; }, ARROW_COALESCE_MS);
				suppressHistoryRef.current = true;
			};

			// Frecce: sposta le gabbie selezionate (Shift = passo grande). Senza Ctrl/Cmd.
			// Si muovono come un blocco: al bordo pagina si ferma tutto il gruppo, invece di
			// schiacciare le gabbie una sull'altra.
			const ARROW_STEPS = {arrowup: [0, -1], arrowdown: [0, 1], arrowleft: [-1, 0], arrowright: [1, 0]};
			if (ARROW_STEPS[normalizedKey] && selectedIds.length && !e.ctrlKey && !e.metaKey)
			{
				e.preventDefault();
				beginKeyboardBurst("move");

				const [dirX, dirY] = ARROW_STEPS[normalizedKey];
				const stepPreview = e.shiftKey ? 10 : 1; // px pagina (stesso spazio delle coordinate gabbia)
				const dx = stepPreview * dirX;
				const dy = stepPreview * dirY;
				const pageW = stageBaseWidth, pageH = stageBaseHeight;
				setZones((prev) =>
				{
					const moves = new Map(prev
						.filter((zone) => selectedIdsSet.has(zone.id))
						.map((zone) => [zone.id, {x: zone.x + dx, y: zone.y + dy}]));
					return applyZoneGeometryUpdates(prev, moves, pageW, pageH);
				});
				return;
			}

			// Cmd/Ctrl+. e Cmd/Ctrl+, (con Option/Alt passo 5%): scala le gabbie selezionate della
			// pagina a schermo dal centro del loro riquadro comune, come in InDesign. Il browser
			// non deve reagire nemmeno senza selezione: Cmd+, aprirebbe le impostazioni di Chrome.
			const scaleFactor = getZoneScaleFactor(e);
			if (scaleFactor)
			{
				e.preventDefault();
				const pageW = stageBaseWidth, pageH = stageBaseHeight;
				const scaleTargets = (list) => list.filter((zone) =>
					zone.pageIndex === currentPageIndex && selectedIdsSet.has(zone.id) && !processingSerials.has(zone.id));
				if (!scaleZonesFromCenter(scaleTargets(zones), scaleFactor, pageW, pageH)) return;
				beginKeyboardBurst("scale");
				setZones((prev) =>
				{
					const scaled = scaleZonesFromCenter(scaleTargets(prev), scaleFactor, pageW, pageH) || [];
					const byId = new Map(scaled.map((zone) => [zone.id, zone]));
					return prev.map((zone) => byId.get(zone.id) || zone);
				});
				return;
			}

			const hasModifier = e.ctrlKey || e.metaKey;
			if (!hasModifier) return;

			// Annulla (Cmd/Ctrl+Z) e Rifai (Cmd/Ctrl+Shift+Z) sullo storico strutturale delle zone.
			// prevZonesRef.current è lo stato corrente affidabile: lo si sposta sulla pila opposta.
			if (normalizedKey === "z")
			{
				// Chiude un'eventuale sequenza di frecce, così il prossimo nudge sarà un passo a sé.
				arrowBurstRef.current = false;
				if (arrowTimerRef.current) clearTimeout(arrowTimerRef.current);
				if (e.shiftKey)
				{
					if (!redoStackRef.current.length) return;
					e.preventDefault();
					restoringRef.current = true;
					undoStackRef.current.push(prevZonesRef.current);
					if (undoStackRef.current.length > 50) undoStackRef.current.shift();
					const next = redoStackRef.current.pop();
					setSelectedIds([]);
					setZones(next);
					return;
				}
				if (!undoStackRef.current.length) return;
				e.preventDefault();
				restoringRef.current = true;
				redoStackRef.current.push(prevZonesRef.current);
				if (redoStackRef.current.length > 50) redoStackRef.current.shift();
				const previous = undoStackRef.current.pop();
				setSelectedIds([]);
				setZones(previous);
				return;
			}

			if (normalizedKey === "c" && selectedIds.length)
			{
				const zonesToCopy = zones.filter((zone) => selectedIdsSet.has(zone.id));
				if (!zonesToCopy.length) return;

				const originX = zonesToCopy.reduce((min, zone) => Math.min(min, zone.x), Number.POSITIVE_INFINITY);
				const originY = zonesToCopy.reduce((min, zone) => Math.min(min, zone.y), Number.POSITIVE_INFINITY);

				e.preventDefault();
				clipboardZoneRef.current = {
					originX,
					originY,
					zones: zonesToCopy.map((zone) => ({
						id: zone.id,
						pageIndex: zone.pageIndex,
						type: zone.type,
						x: zone.x,
						y: zone.y,
						w: zone.w,
						h: zone.h
					}))
				};
				return;
			}

			// Tasto V rilevato via e.code (fisico): su Mac con Option/Alt premuto e.key diventa un
			// carattere speciale (es. "√"), quindi non ci si può basare su normalizedKey per il combo Alt.
			if (e.code === "KeyV" || normalizedKey === "v")
			{
				const clipboardPayload = clipboardZoneRef.current;
				const copiedZones = clipboardPayload?.zones || [];
				if (!copiedZones.length) return;
				if (!pages.length) return;

				// Cmd/Ctrl + Shift + Alt(Option) + V = incolla nella STESSA posizione dell'originale
				// (standard InDesign); qualsiasi altra combinazione con V = incolla al puntatore/centro.
				const inPlace = e.shiftKey && e.altKey;

				const pointerPos = lastPointerPosRef.current;
				const hasPointerOnCurrentPage = pointerPos
					&& pointerPos.pageIndex === currentPageIndex
					&& Number.isFinite(pointerPos.x)
					&& Number.isFinite(pointerPos.y)
					&& pointerPos.x >= 0
					&& pointerPos.y >= 0
					&& pointerPos.x <= stageBaseWidth
					&& pointerPos.y <= stageBaseHeight;

				const anchor = hasPointerOnCurrentPage
					? {x: pointerPos.x, y: pointerPos.y}
					: {x: stageBaseWidth / 2, y: stageBaseHeight / 2};

				const anchorX = toFull(anchor.x);
				const anchorY = toFull(anchor.y);

				e.preventDefault();

				// Seriali prenotati atomicamente dal server (univoci a livello promo).
				(async () =>
				{
					const reserved = await reserveSerials(copiedZones.length);
					let ids;
					if (reserved.status === "ok")
						ids = reserved.serials;
					else if (reserved.status === "no-promo")
					{
						const maxId = zones.reduce((max, zone) => Math.max(max, zone.id), 0);
						ids = copiedZones.map((_, index) => maxId + index + 1);
					}
					else
					{
						message.error({content: "Impossibile assegnare i seriali: rete non disponibile. Riprova.", key: "reserve-serial"});
						return;
					}

					// Ogni copia incollata viene vincolata dentro i bordi pagina (niente sconfinamenti).
					const pastedZones = copiedZones.map((zone, index) => clampZoneToPage({
						id: ids[index],
						pageIndex: currentPageIndex,
						type: zone.type === "banner" ? "banner" : "gabbia",
						x: inPlace ? zone.x : anchorX + (zone.x - clipboardPayload.originX),
						y: inPlace ? zone.y : anchorY + (zone.y - clipboardPayload.originY),
						w: zone.w,
						h: zone.h
					}, stageBaseWidth, stageBaseHeight));

					setZones((prev) => [...prev, ...pastedZones]);
					setSelectedIds(pastedZones.map((zone) => zone.id));
				})();
			}
		};
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [currentPageIndex, handleZonesChange, message, modal, pages.length, processingSerials, reserveSerials, selectedIds.length, selectedIdsSet, stageBaseHeight, stageBaseWidth, toFull, zones]);

	if (loading && Object.keys(loadedImages).length === 0)
	{
		return <Spin size="large" tip="Caricamento immagini PDF..." fullscreen/>;
	}

	const hasPages = pages.length > 0;
	const aiInProgress = aiProcessing && ocrProgress > 0 && ocrProgress < 100;
	const aiDisabled = aiProcessing || zones.length === 0;

	// Gabbie selezionate (rielaborazione mirata) e gabbie della pagina corrente.
	const selectedGabbie = zones.filter(z => z.type === "gabbia" && selectedIdsSet.has(z.id));
	const pageGabbie = zones.filter(z => z.type === "gabbia" && z.pageIndex === currentPageIndex);

	// Solo le operazioni che agiscono su questa pagina vivono nel menu overflow:
	// scala senza affollare la toolbar quando verranno aggiunti nuovi pulsanti.
	// La navigazione "Gestione Volantino" resta invece un pulsante diretto sempre
	// visibile, per dare al designer un accesso rapido al cambio di contesto.
	const actionMenuItems = [];
	if (hasPages)
	{
		actionMenuItems.push({key: "reloadpdf", icon: <CloudUploadOutlined/>, label: "Ricarica PDF"});
	}
	if (canUnlockProcessing)
	{
		if (actionMenuItems.length) actionMenuItems.push({type: "divider"});
		actionMenuItems.push({
			key: "unlock",
			icon: <DisconnectOutlined/>,
			danger: true,
			label: flyerState?.status === "ERROR" ? "Sblocca errore" : "Sblocca elaborazione"
		});
	}

	const onActionMenuClick = ({key}) =>
	{
		if (key === "unlock") handleUnlockProcessing();
		else if (key === "reloadpdf") setPdfModalOpen(true);
	};

	// Rielaborazione forzata: sovrascrive anche le card gia' complete/convalidate, quindi
	// passa sempre da una conferma esplicita (il click diretto sul pulsante elabora invece
	// le sole gabbie nuove e non puo' distruggere lavoro).
	const confirmForcedReprocess = ({title, content, serials = null}) => modal.confirm({
		title,
		content,
		okText: "Rielabora",
		okButtonProps: {danger: true},
		cancelText: "Annulla",
		onOk: () => handleAiProcessing(serials ? {serials, forceReprocess: true} : {forceReprocess: true})
	});

	const aiMenuItems = [
		{
			key: "only-new",
			icon: <CloudUploadOutlined/>,
			label: "Solo le gabbie nuove",
			disabled: aiDisabled
		},
		{type: "divider"},
		{
			key: "reprocess-page",
			icon: <RedoOutlined/>,
			label: `Rielabora pagina ${currentPageIndex + 1} (${pageGabbie.length})`,
			disabled: aiDisabled || pageGabbie.length === 0
		},
		{
			key: "reprocess",
			icon: <RedoOutlined/>,
			danger: true,
			label: "Rielabora tutto il volantino",
			disabled: aiDisabled
		}
	];

	const onAiMenuClick = ({key}) =>
	{
		if (key === "only-new")
			handleAiProcessing();
		else if (key === "reprocess-page")
			confirmForcedReprocess({
				title: `Rielaborare tutte le gabbie di pagina ${currentPageIndex + 1}?`,
				content: "Le card già elaborate di questa pagina, comprese quelle convalidate, verranno riestratte dall'AI e sovrascritte.",
				serials: pageGabbie.map(z => z.id)
			});
		else if (key === "reprocess")
			confirmForcedReprocess({
				title: "Rielaborare tutto il volantino?",
				content: "Tutte le card già elaborate, comprese quelle convalidate, verranno riestratte dall'AI e sovrascritte."
			});
	};

	const primaryAction = hasPages ? (
		// Split button: il click diretto elabora le SOLE gabbie nuove (azione non distruttiva),
		// la freccia espone le rielaborazioni forzate. Il pulsante principale resta separato
		// per conservare la barra di avanzamento a gradiente durante l'elaborazione.
		<Space.Compact>
			<Button
				type="primary"
				icon={<CloudUploadOutlined/>}
				onClick={() => handleAiProcessing()}
				loading={aiInProgress}
				style={aiInProgress ? {
					background: `linear-gradient(90deg, #ff9800 ${ocrProgress}%, #f0f0f0 ${ocrProgress}%)`,
					borderColor: "#ff9800"
				} : undefined}
				disabled={aiDisabled}
			>
				{aiInProgress ? `Pre-el. in corso (${ocrProgress}%)` : "Elaborazione con AI"}
			</Button>
			<Dropdown trigger={["click"]} menu={{items: aiMenuItems, onClick: onAiMenuClick}} disabled={aiDisabled}>
				<Tooltip title="Scegli cosa elaborare">
					<Button type="primary" icon={<DownOutlined/>} disabled={aiDisabled}/>
				</Tooltip>
			</Dropdown>
		</Space.Compact>
	) : (
		<Button type="primary" icon={<CloudUploadOutlined/>} onClick={() => setPdfModalOpen(true)}>
			Carica PDF
		</Button>
	);

	return (
		<div className={`preprocess-page ${isSidebarOpen ? "" : "pages-collapsed"}`}>
			<div className="preprocess-header">
				<div className="preprocess-header-left">
					<Button type="text" icon={<ArrowLeftOutlined/>} onClick={() => navigate(-1)}>Indietro</Button>
					<Title level={4} className="preprocess-title">Pre-elaborazione</Title>

					<Divider type="vertical" className="toolbar-divider"/>

					{/* Stepper: un solo controllo con bordo unico invece di tre riquadri separati
					    (freccia | testo | freccia). I bottoni interni sono type="text" (senza bordo
					    proprio) e le hairline le disegna .toolbar-stepper sul testo centrale via CSS. */}
					<div className="toolbar-group toolbar-stepper">
						<Tooltip title="Pagina precedente">
							<Button
								type="text"
								icon={<LeftOutlined/>}
								onClick={() => changePage(Math.max(0, currentPageIndex - 1))}
								disabled={!canGoPrev}
							/>
						</Tooltip>
						<PageJumper current={currentPageIndex} total={totalPages} onGo={changePage}>
							<Text strong className="toolbar-pageinfo">
								{totalPages ? `Pag. ${currentPageIndex + 1} / ${totalPages}` : "Pag. 0 / 0"}
							</Text>
						</PageJumper>
						<Tooltip title="Pagina successiva">
							<Button
								type="text"
								icon={<RightOutlined/>}
								onClick={() => changePage(Math.min(totalPages - 1, currentPageIndex + 1))}
								disabled={!canGoNext}
							/>
						</Tooltip>
					</div>

					<Divider type="vertical" className="toolbar-divider"/>

					<div className="toolbar-group toolbar-stepper">
						<Tooltip title="Zoom indietro"><Button type="text" icon={<ZoomOutOutlined/>} onClick={() => setScale(s => Math.max(0.1, s - 0.1))}/></Tooltip>
						<Text strong className="toolbar-zoom">{Math.round(scale * 100)}%</Text>
						<Tooltip title="Zoom avanti"><Button type="text" icon={<ZoomInOutlined/>} onClick={() => setScale(s => Math.min(2, s + 0.1))}/></Tooltip>
					</div>

					{pdfPageSize && (
						<>
							<Divider type="vertical" className="toolbar-divider"/>
							<Tooltip title="Formato pagina del PDF caricato">
								<Text type="secondary" className="toolbar-pdfsize">{pdfPageSize}</Text>
							</Tooltip>
						</>
					)}

					{saving && <Spin size="small"/>}
				</div>

				<div className="preprocess-toolbar">
					<div className="toolbar-group">
						<Segmented
							value={activeType}
							onChange={setActiveType}
							options={[
								{value: "banner", label: <span className="toolbar-type-option"><span className="toolbar-type-dot toolbar-type-dot--banner"/>Banner</span>},
								{value: "gabbia", label: <span className="toolbar-type-option"><span className="toolbar-type-dot toolbar-type-dot--gabbia"/>Gabbia</span>}
							]}
						/>
						<Popover
							trigger="click"
							placement="bottom"
							content={(
								<div className="toolbar-style-pop">
									<div className="style-row">
										<Text type="secondary">Spessore bordo</Text>
										<InputNumber
											min={1}
											max={10}
											value={rectStrokeWidth}
											onChange={v => setRectStrokeWidth(v || 1)}
											size="small"
											style={{width: 80}}
										/>
									</div>
									<div className="style-row">
										<Text type="secondary">Opacità ({Math.round(rectOpacity * 100)}%)</Text>
										<Slider
											min={0}
											max={1}
											step={0.05}
											value={rectOpacity}
											onChange={v => setRectOpacity(v)}
											tooltip={{open: false}}
										/>
									</div>
								</div>
							)}
						>
							<Tooltip title="Stile gabbie">
								<Button icon={<BgColorsOutlined/>}>Stile</Button>
							</Tooltip>
						</Popover>
						<Tooltip title={showSerials ? "Nascondi i seriali sulle gabbie" : "Mostra i seriali sulle gabbie"}>
							<Button
								icon={<NumberOutlined/>}
								type={showSerials ? "primary" : "default"}
								onClick={() => setShowSerials(v => !v)}
							/>
						</Tooltip>
						<ShortcutsLegend/>
					</div>

					<Divider type="vertical" className="toolbar-divider"/>

					<div className="toolbar-group">
						{primaryAction}
						{/* Sempre montato e sempre visibile (mai un mount/unmount condizionale): prima
						    appariva/spariva dal DOM solo con ≥2 gabbie selezionate, e la sua comparsa a
						    piena larghezza spingeva il gruppo a capo su una riga in più — .preprocess-header
						    è grid-column 1/-1 dentro .preprocess-page (righe auto/1fr), quindi quel capo-riga
						    cambiava l'altezza dell'header e spingeva giù tutto il canvas sotto (il
						    "saltellamento" segnalato). Restando sempre montato occupa sempre il proprio
						    spazio nella riga, quindi il numero di righe della toolbar non cambia mai;
						    resta disabilitato solo a selezione vuota (nulla da rielaborare). */}
						<Button
							icon={<RedoOutlined/>}
							onClick={() => handleAiProcessing({serials: selectedGabbie.map(z => z.id), forceReprocess: true})}
							disabled={aiDisabled || !selectedGabbie.length}
						>
							Rielabora selezione ({selectedGabbie.length})
						</Button>
						{actionMenuItems.length > 0 && (
							<Dropdown
								menu={{items: actionMenuItems, onClick: onActionMenuClick}}
								trigger={["click"]}
							>
								<Button>Azioni <DownOutlined/></Button>
							</Dropdown>
						)}
						<Button
							icon={<AppstoreOutlined/>}
							onClick={() => navigate(`/flyermanage/${flyerId}`)}
						>
							Gestione Volantino
						</Button>
					</div>
				</div>
			</div>

			<div className={`preprocess-pages-sidebar ${isSidebarOpen ? "" : "collapsed"}`}>
				<div className="pages-sidebar-head">
					{/* Maniglia sul bordo del pannello: comprimere/espandere è un'azione di questo
					    pannello, va cercata qui e non su un bottone lontano in cima alla toolbar.
					    A pannello compresso resta una striscia sottile (28px, vedi .pages-collapsed
					    in CSS) con solo questa freccia, mai a larghezza 0 — altrimenti non ci sarebbe
					    più modo di riespanderlo se non tornando con lo sguardo alla toolbar. */}
					<Tooltip title={isSidebarOpen ? "Comprimi elenco pagine" : "Espandi elenco pagine"} placement="right">
						<button
							type="button"
							className="pages-sidebar-toggle"
							onClick={() => setIsSidebarOpen(open => !open)}
							aria-label={isSidebarOpen ? "Comprimi elenco pagine" : "Espandi elenco pagine"}
						>
							{isSidebarOpen ? <LeftOutlined/> : <RightOutlined/>}
						</button>
					</Tooltip>
					{isSidebarOpen && <Title level={5} className="pages-title">Pagine</Title>}
					{isSidebarOpen && (
						<Tooltip title="Aggiungi pagine" placement="right">
							<button
								type="button"
								className="pages-sidebar-add"
								onClick={openAddPages}
								aria-label="Aggiungi pagine"
							>
								<PlusOutlined/>
							</button>
						</Tooltip>
					)}
				</div>
				{isSidebarOpen && (
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragEnd={handlePagesDragEnd}
						modifiers={[restrictToVerticalAxis]}
					>
						<SortableContext
							items={pages.map((page, idx) => page.name || String(idx))}
							strategy={verticalListSortingStrategy}
						>
							<div className="pages-list">
								{pages.map((page, idx) =>
								{
									const rawUrl = page.previewUrl || page.url;
									const thumbSrc = rawUrl ? (rawUrl.startsWith("http") ? rawUrl : `${apiBaseUrl}${rawUrl}`) : "";
									return (
										<SortablePageThumb
											key={page.name || idx}
											page={page}
											index={idx}
											isActive={idx === currentPageIndex}
											zoneCount={zonesByPage.get(idx) || 0}
											thumbSrc={thumbSrc}
											onSelect={changePage}
											onMenuAction={handlePageMenuAction}
										/>
									);
								})}
							</div>
						</SortableContext>
					</DndContext>
				)}
			</div>

			<div className="preprocess-zones-sidebar">
				<Title level={5} className="sidebar-title zones-sidebar-title">
					{(() =>
					{
						const selectedCount = zones.filter((zone) => selectedIdsSet.has(zone.id)).length;
						const allSelected = zones.length > 0 && selectedCount === zones.length;
						return (
							<Tooltip title={allSelected ? "Deseleziona tutte le gabbie del volantino" : "Seleziona tutte le gabbie del volantino"}>
								<Checkbox
									className="zones-select-all-checkbox"
									checked={allSelected}
									indeterminate={selectedCount > 0 && !allSelected}
									disabled={!zones.length}
									onClick={(event) => handleSelectAllZonesGlobal(allSelected, event)}
								/>
							</Tooltip>
						);
					})()}
					<span>Gabbie tracciate ({zones.length})</span>
				</Title>

				<Input
					className="zones-search"
					size="small"
					allowClear
					prefix={<SearchOutlined/>}
					placeholder="Cerca seriale o prodotto…"
					value={zoneQuery}
					onFocus={loadCardTexts}
					onChange={(e) => setZoneQuery(e.target.value)}
				/>

				<div className="zones-list">
					{zoneSearchResults ? (
						zoneSearchResults.length ? (
							<div className="zones-search-results">
								<Text type="secondary" className="zones-search-count">
									{zoneSearchResults.length} {zoneSearchResults.length === 1 ? "risultato" : "risultati"} in tutto il volantino
									{cardTextLoading ? " · carico i prodotti…" : ""}
								</Text>
								{zoneSearchResults.map((zone) => (
									<ZoneResultItem
										key={zone.id}
										zone={zone}
										isSelected={selectedIdsSet.has(zone.id)}
										isProcessing={processingSerials.has(zone.id)}
										description={cardTextBySerial?.get(zone.id)?.label}
										onSelect={(event) => handleZoneSelection(zone.id, zone.pageIndex, event, {source: "sidebar"})}
										onDelete={(id) =>
										{
											setZones(prev => prev.filter(item => item.id !== id));
											setSelectedIds(prev => prev.filter((selectedZoneId) => selectedZoneId !== id));
										}}
									/>
								))}
							</div>
						) : (
							<Empty
								image={Empty.PRESENTED_IMAGE_SIMPLE}
								description={cardTextLoading ? "Carico i prodotti…" : "Nessuna gabbia trovata"}
								className="zones-empty"
							/>
						)
					) : groupedZones.length ? (
						<Collapse
							accordion
							activeKey={activeZonePanelKey}
							onChange={handleZonePanelChange}
							className="zones-collapse"
							items={groupedZones.map((group) => ({
								key: String(group.pageIndex),
								label: (() =>
								{
									const selectedCount = group.zones.filter((zone) => selectedIdsSet.has(zone.id)).length;
									const allSelected = selectedCount === group.zones.length;
									return (
										<div className="zones-page-panel-header">
											<Tooltip title={allSelected ? "Deseleziona tutte le gabbie di questa pagina" : "Seleziona tutte le gabbie di questa pagina"}>
												<Checkbox
													className="zones-select-all-checkbox"
													checked={allSelected}
													indeterminate={selectedCount > 0 && !allSelected}
													onClick={(event) => handleSelectAllZonesOnPage(group, allSelected, event)}
												/>
											</Tooltip>
											<Space size={6}>
												<Text strong>Pagina {group.pageIndex + 1}</Text>
												<Text type="secondary">
													{group.zones.length} {group.zones.length === 1 ? "gabbia" : "gabbie"}
												</Text>
											</Space>
										</div>
									);
								})(),
								children: (
									<DndContext
										sensors={sensors}
										collisionDetection={closestCenter}
										onDragEnd={(event) => handlePageDragEnd(group.pageIndex, event)}
										modifiers={[restrictToVerticalAxis]}
									>
										<SortableContext
											items={group.zones.map((zone) => zone.id)}
											strategy={verticalListSortingStrategy}
										>
											<div className="zones-page-panel-list">
												{group.zones.map((z) => (
													<SortableZoneItem
														key={z.id}
														zone={z}
														isSelected={selectedIdsSet.has(z.id)} isProcessing={processingSerials.has(z.id)}
														onSelect={(event) =>
														{
															handleZoneSelection(z.id, z.pageIndex, event, {source: "sidebar"});
														}}
														onDelete={(id) =>
														{
															setZones(prev => prev.filter(item => item.id !== id));
															setSelectedIds(prev => prev.filter((selectedZoneId) => selectedZoneId !== id));
														}}
													/>
												))}
											</div>
										</SortableContext>
									</DndContext>
								)
							}))}
						/>
					) : (
						<Empty
							image={Empty.PRESENTED_IMAGE_SIMPLE}
							description="Nessuna gabbia tracciata"
							className="zones-empty"
						/>
					)}
				</div>
			</div>

			<div
				ref={scrollContainerRef}
				className="preprocess-canvas-area"
				style={{backgroundColor: "#f0f2f5"}}
			>
				<div className="preprocess-canvas-wrapper">
					{currentPage && (
						<Text className="preprocess-current-page-label">Pagina {currentPageIndex + 1}</Text>
					)}
					<Profiler id="PreprocessCanvas" onRender={handleProfilerRender}>
						<Stage
							ref={stageRef}
							width={stageWidth}
							height={stageHeight}
							x={STAGE_MARGIN}
							y={STAGE_MARGIN}
							scaleX={scale}
							scaleY={scale}
							onMouseDown={handleMouseDown}
							onMouseMove={handleMouseMove}
							onMouseUp={handleMouseUp}
							className="konva-stage-wrapper"
						>
							<Layer listening={false}>
								{currentImage && currentPage && (
									<Group key={`page-${currentPageIndex}`} y={0}>
										{/* Immagine PDF */}
										<KonvaImage
											name="page-image"
											image={currentImage}
											width={stageBaseWidth}
											height={stageBaseHeight}
											perfectDrawEnabled={false}
										/>
									</Group>
								)}
							</Layer>

							<Layer>
								{/* Rettangolo temporaneo durante il disegno */}
								<Rect ref={previewRectRef} visible={false} listening={false}/>

								{/* Rendering di tutte le zone - Renderizza solo se il layout è pronto */}
								{currentImage && zones.filter(zone => zone.pageIndex === currentPageIndex).map((zone) =>
								{

									const baseStyle = getZoneColor(zone);
									const dynamicStyle = {
										stroke: baseStyle.stroke,
										strokeWidth: rectStrokeWidth,
										fill: baseStyle.fill.replace("1)", `${rectOpacity})`)
									};


										return (
											<React.Fragment key={zone.id}>
											<TraceableRect
												key={zone.id}
												shapeProps={{
													name: "zone-rect",
													id: String(zone.id),
													type: zone.type,
													x: toPreview(zone.x),
													y: toPreview(zone.y),
													width: toPreview(zone.w),
													height: toPreview(zone.h)
												}}
												isSelected={selectedIdsSet.has(zone.id)}
												isProcessing={processingSerials.has(zone.id)}
												searchState={getZoneSearchState(searchMatchIds, zone.id)}
												zoom={scale}
												onSelect={(event) => handleZoneSelection(zone.id, zone.pageIndex, event)}
												onContextMenu={(e) => handleZoneContextMenu(zone.id, e)}
												style={dynamicStyle}
												onChange={(newProps) =>
												{
													// Riconverte in coordinate pagina; il vincolo dentro i bordi pagina
													// (della gabbia o dell'intero gruppo spostato/ridimensionato) lo
													// applica queueZoneGeometry insieme alle altre gabbie del gesto.
													queueZoneGeometry(zone.id, {
														x: toFull(newProps.x),
														y: toFull(newProps.y),
														w: toFull(newProps.width),
														h: toFull(newProps.height)
													});
												}}
											/>
										{zone.type === "gabbia" && (
											<ManualZoneBadge x={toPreview(zone.x)} y={toPreview(zone.y)} overrides={zone.manualOverrides} labels={zone.manualLabels} fieldNames={zone.type === "banner" ? fieldNamesByZoneType.banner : fieldNamesByZoneType.gabbia} fieldColors={fieldColorByKey}/>
										)}
										{showSerials && (
											<ZoneSerialBadge
												x={toPreview(zone.x)} y={toPreview(zone.y)} w={toPreview(zone.w)}
												seriale={zone.id}
												color={baseStyle.stroke}
												dimmed={getZoneSearchState(searchMatchIds, zone.id) === "dim"}
											/>
										)}
										</React.Fragment>
);
								})}

								{/* Maniglie della selezione (una o più gabbie), sopra le gabbie */}
								<ZoneSelectionTransformer nodeIds={transformerZoneIds} pageW={stageBaseWidth} pageH={stageBaseHeight}/>
							</Layer>
						</Stage>
					</Profiler>
				</div>
			</div>

			{zoneMenu && (
				<ZoneContextMenu
					x={zoneMenu.x}
					y={zoneMenu.y}
					targetCount={zoneMenu.targetIds.length}
					reprocessCount={zoneMenu.reprocessTargetIds.length}
					fields={zoneMenu.kind === "banner" ? bannerMenuFields : manualFields}
					reprocessDisabled={aiProcessing}
					onReprocess={zoneMenu.reprocessTargetIds.length ? () => handleAiProcessing({serials: zoneMenu.reprocessTargetIds, forceReprocess: true}) : undefined}
					onOpenFields={() => openManualFields(zoneMenu.targetIds, zoneMenu.kind)}
					onEditSerial={zoneMenu.targetIds.length === 1 ? () => setSerialEditZone(zones.find(z => z.id === zoneMenu.targetIds[0]) || null) : undefined}
					editSerialDisabledReason={zoneMenu.targetIds.length === 1 && processingSerials.has(zoneMenu.targetIds[0]) ? "Gabbia in elaborazione" : ""}
					onClose={() => setZoneMenu(null)}
				/>
			)}

			<EditSerialModal
				open={!!serialEditZone}
				flyerId={flyerId}
				zone={serialEditZone}
				localSerials={new Set(zones.filter(z => z.id !== serialEditZone?.id).map(z => z.id))}
				onConfirm={(newSerial) =>
				{
					const oldSerial = serialEditZone.id;
					setZones(prev => prev.map(z => z.id === oldSerial ? {...z, id: newSerial} : z));
					setSelectedIds(prev => prev.map(id => id === oldSerial ? newSerial : id));
					setSerialEditZone(null);
					message.success(`Seriale #${oldSerial} → #${newSerial}`);
				}}
				onCancel={() => setSerialEditZone(null)}
			/>

			<ManualFieldModal
				open={!!manualModal}
				fields={manualModal?.fields || []}
				cooperativaId={flyerState?.cooperativaId}
				targetCount={manualModal?.targetIds?.length || 0}
				initialValues={manualModal?.initialValues || {}}
				onApply={handleApplyManual}
				onCancel={() => setManualModal(null)}
			/>

			<UploadPdfModal
				flyerId={flyerId}
				open={pdfModalOpen}
				onClose={() => setPdfModalOpen(false)}
				previewScaleDefault={previewScale}
				preserveZones={zones.length > 0}
				onComplete={() =>
				{
					setPdfModalOpen(false);
					setScale(DEFAULT_SCALE);
					setLoadedImages({});
					fetchData(); // Ricarica le nuove pagine
				}}
			/>

			<AddPagesModal
				flyerId={flyerId}
				open={addPagesOpen}
				pageCount={pages.length}
				onClose={closeAddPages}
				onCompleted={handleAddPagesCompleted}
			/>
		</div>
	);
}
